import { useQueryClient } from "@tanstack/react-query"
import { createPortal } from "react-dom"
import { Select, Tooltip } from "antd"
import MarkdownRender from "~/components/markdown-render"
import React, { useEffect, useMemo, useRef, useState } from "react"

import "./dialer.css"
import I from "./icons"
import { DIAL_CODES, dialCodeOptions } from "~/utils/dial-codes"
import { DEMO_LISTS, DEMO_REP } from "./dummy-data"
import useVoiceSessionStore from "~/store/voice-session"
import useGlobalStore from "~/store/global"
import { useVoiceDevice } from "~/hooks/use-voice-device"
import { getVoiceNumbers, setCallHold } from "~/api/voice"
import PersonBriefHost from "./person-brief-host"
import { useEnrichPeople, useResolveCallPhone } from "~/hooks/people-os"
import { isDialablePhoneNumber as isDialable } from "~/utils/format-phone-number"
import { sanitizeUrl } from "~/utils/helpers"
import {
  buyDialerNumber,
  entryPersonId,
  getDialerCallHistory,
  getDialerCallSummary,
  getDialerTalkingPoints,
  searchDialerNumbers
} from "~/api/dialer"

const DL_TERMINAL_STATUSES = [
  "COMPLETED",
  "NO_ANSWER",
  "FAILED",
  "OUTSIDE_HOURS"
]

export const COUNTRY_LABEL = {
  IN: "India",
  US: "United States",
  GB: "United Kingdom"
}

// Fetched per contact rather than for the whole queue: the rep may reorder or skip,
// and a miss also tells the backend to generate, so asking for the person in front of
// them is what keeps the next one warm.
const SUM_POLL_MS = 5000
const SUM_POLL_CEILING_MS = 180000
const TKP_POLL_MS = 3000
const TKP_STEP_MS = 2600
// Narrated in the order the consumer actually works: profile fetch, then the read,
// then the write. A static line gave no sense of progress across a twenty-second wait.
const TKP_STEPS = [
  "Pulling their LinkedIn profile",
  "Reading their role and recent moves",
  "Matching them to this opportunity",
  "Writing your openers"
]

function TalkingPointsLoader() {
  const [step, setStep] = useState(0)

  useEffect(() => {
    const t = setInterval(
      () => setStep((n) => (n + 1) % TKP_STEPS.length),
      TKP_STEP_MS
    )
    return () => clearInterval(t)
  }, [])

  return (
    <div className="tkp-gen">
      <span className="tkp-gen-dot" />
      <span className="tkp-gen-tx" key={step}>
        {TKP_STEPS[step]}…
      </span>
    </div>
  )
}
// How long after PICKUP to keep asking before deciding generation failed.
// Generation is kicked off server-side only when the call connects and normally
// lands in a few seconds; this is the ceiling measured from that pickup, not from
// when the phone started ringing, so a long ring never eats into the wait.
const TKP_GIVEUP_MS = 30000

// Generation is kicked off server-side when the call CONNECTS (pickup), never
// while ringing and never for a call that is not answered. The panel reflects
// that: it promises generation "once picked up" while ringing, shows progress
// once it is generating, and only says it could not generate after a real pickup
// produced nothing — never for a call that was never answered.
// Exported so the in-row panel uses THIS hook rather than the people-os one of
// the same name. They hit different endpoints: this reads
// dialer_talking_points (written server-side on pickup), the other runs the
// Person-360 generator. The in-row panel was calling the wrong one, so a call
// with four freshly generated points still rendered "0/0".
export function useDialerTalkingPoints(
  tokenInfo,
  peopleId,
  active = true,
  freshAfter = 0,
  live = false
) {
  const [state, setState] = useState({
    points: [],
    generating: false,
    unavailable: false,
    stale: false
  })
  const activeRef = useRef(active)
  activeRef.current = active
  // Whether the call was ever picked up. Generation is only attempted after
  // pickup, so "could not generate" must be gated on this — a ringing or
  // never-answered call has not attempted anything and must not claim failure.
  const liveRef = useRef(live)
  liveRef.current = live
  const attemptedAtRef = useRef(0)
  const tenantId = tokenInfo?.tenant_id

  useEffect(() => {
    if (!tenantId || !peopleId) {
      attemptedAtRef.current = 0
      setState({
        points: [],
        generating: false,
        unavailable: false,
        stale: false
      })
      return
    }

    let cancelled = false
    let timer = null
    attemptedAtRef.current = 0
    setState({
      points: [],
      generating: true,
      unavailable: false,
      stale: false
    })

    // Attempted once picked up, and the give-up clock runs from that moment.
    const markAttempt = () => {
      if (liveRef.current && !attemptedAtRef.current) {
        attemptedAtRef.current = Date.now()
      }
    }
    const attempted = () => attemptedAtRef.current > 0
    const gaveUp = () =>
      attempted() && Date.now() - attemptedAtRef.current >= TKP_GIVEUP_MS

    const tick = () => {
      markAttempt()
      getDialerTalkingPoints(tokenInfo, peopleId)
        .then((res) => {
          if (cancelled) return
          markAttempt()
          // A slot the generator could not support comes back null — drop those
          // rather than rendering an empty numbered line.
          const filled = (res?.data?.points || []).filter((p) => p?.title)
          const generatedAt = res?.data?.generated_at
            ? new Date(res.data.generated_at).getTime()
            : 0
          // Points written before this call connected are the previous call's. They are
          // still worth showing while the new set is produced, but polling must not stop
          // on them or the rep never sees anything from the conversation they just had.
          const isStale = freshAfter > 0 && generatedAt <= freshAfter

          if (filled.length && !isStale) {
            setState({
              points: filled,
              generating: false,
              unavailable: false,
              stale: false
            })
            return
          }
          if (filled.length && isStale) {
            setState({
              points: filled,
              generating: true,
              unavailable: false,
              stale: true
            })
          }
          // Stop asking once the call is over or the post-pickup wait is up.
          // "unavailable" is set only when a pickup actually happened and still
          // produced nothing; a never-answered call ends with it false.
          if (!activeRef.current || gaveUp()) {
            setState({
              points: filled,
              generating: false,
              unavailable: attempted() && !filled.length,
              stale: isStale
            })
            return
          }
          timer = setTimeout(tick, TKP_POLL_MS)
        })
        .catch(() => {
          if (cancelled) return
          if (!activeRef.current || gaveUp()) {
            setState({
              points: [],
              generating: false,
              unavailable: attempted()
            })
            return
          }
          timer = setTimeout(tick, TKP_POLL_MS)
        })
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [tenantId, peopleId, freshAfter])

  return state
}

const NUM_COUNTRIES = [
  { code: "US", dial: "+1", flag: "🇺🇸", example: "408316" },
  { code: "GB", dial: "+44", flag: "🇬🇧", example: "2079" },
  { code: "DE", dial: "+49", flag: "🇩🇪", example: "3055" }
]
const NUM_MATCHES = [
  { id: "start", label: "Starts with" },
  { id: "anywhere", label: "Anywhere" },
  { id: "end", label: "Ends with" }
]

// No design exists for this yet. It stays deliberately plain: pick a country, type the
// digits you want, say where they should sit, buy one from the list.
const ANM_SAMPLE_CITIES = {
  US: ["San Jose, CA", "Oakland, CA", "Austin, TX", "Denver, CO", "Tampa, FL"],
  GB: ["London", "Manchester", "Leeds", "Bristol", "Glasgow"],
  DE: ["Berlin", "Munich", "Hamburg", "Cologne", "Frankfurt"]
}

function anmSamples(country, contains) {
  const meta = NUM_COUNTRIES.find((c) => c.code === country) || NUM_COUNTRIES[0]
  const stem = (contains || meta.example).replace(/[^0-9]/g, "").slice(0, 3)
  const area = stem.padEnd(3, "4")
  return Array.from({ length: 10 }, (_, i) => ({
    phone_number: `${meta.dial} ${area} ${String(200 + i * 37).slice(0, 3)} ${String(1000 + i * 617).slice(0, 4)}`,
    friendly_name: "sample",
    locality: (ANM_SAMPLE_CITIES[country] || ANM_SAMPLE_CITIES.US)[i % 5],
    region: null,
    country,
    sample: true
  }))
}

// Exported so the Boost Connect settings modal opens the SAME buy-a-number
// flow the in-session dialer does, rather than a second one drifting from it.
export function AddNumberModal({
  tokenInfo,
  onClose,
  onBought,
  variant = "dark"
}) {
  const [country, setCountry] = useState("US")
  const [match, setMatch] = useState("anywhere")
  const [contains, setContains] = useState("")
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [buying, setBuying] = useState(null)
  const [err, setErr] = useState(null)
  const [info, setInfo] = useState(null)

  const search = async () => {
    setSearching(true)
    setErr(null)
    setInfo(null)
    try {
      const res = await searchDialerNumbers(tokenInfo, {
        country,
        contains,
        match
      })
      const d = res?.data
      const found = d?.numbers || []
      setResults(found)
      if (d?.limit_reached) {
        setInfo(
          "You already hold the maximum of " + d.max_numbers + " numbers."
        )
      } else if (!(d?.numbers || []).length) {
        setInfo("No numbers matched. Try fewer digits or a different position.")
      } else {
        setInfo(d.remaining + " of " + d.max_numbers + " slots left")
      }
    } catch (e) {
      // Twilio answers 404 for a country it holds no local inventory in, which is an answer
      // rather than a fault, so it is stated plainly instead of as an error.
      const noStock = e?.response?.status === 404 || e?.status === 404
      setResults(anmSamples(country, contains))
      setInfo(
        (noStock
          ? "No numbers available in " + country + " from Twilio"
          : e?.response?.data?.message || "Search unavailable") +
          ". Showing sample rows, which cannot be bought."
      )
    } finally {
      setSearching(false)
    }
  }

  const buy = async (num) => {
    setBuying(num)
    setErr(null)
    try {
      const res = await buyDialerNumber(tokenInfo, num)
      if (onBought) onBought(res?.data?.number)
      if (onClose) onClose()
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not buy that number")
    } finally {
      setBuying(null)
    }
  }

  // Portalled to the body: the options rail animates with a CSS transform, and
  // a transformed ancestor becomes the containing block for position:fixed, so
  // rendered in place this scrim was pinned inside the rail instead of the
  // viewport - drawn behind the manage-numbers modal and out of reach.
  return createPortal(
    <div className="anm-scrim" onMouseDown={onClose}>
      <div
        className={"anm" + (variant === "light" ? " is-light" : "")}
        role="dialog"
        aria-label="Add a number"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bdl-hd">
          <span className="bdl-hd-t">{I.phone({ size: 14 })} Add a number</span>
          <span className="bdl-hd-sp" />
          <button className="dlr-x" onClick={onClose} aria-label="Close">
            {I.close({ size: 16 })}
          </button>
        </div>

        <div className="anm-body">
          <div className="anm-row">
            <select
              className="anm-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              aria-label="Country code"
            >
              {NUM_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.dial} · {c.code}
                </option>
              ))}
            </select>
          </div>

          <div className="anm-row">
            <input
              className="anm-input"
              value={contains}
              onChange={(e) => setContains(e.target.value)}
              placeholder={
                "Optional prefix search, e.g. " +
                (NUM_COUNTRIES.find((c) => c.code === country) || {}).example
              }
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={search}
              disabled={searching}
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </div>

          <div className="anm-row">
            {NUM_MATCHES.map((m) => (
              <button
                type="button"
                key={m.id}
                className={"anm-pill" + (match === m.id ? " is-on" : "")}
                onClick={() => setMatch(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>

          {err ? <div className="anm-err">{err}</div> : null}
          {info && !err ? <div className="anm-info">{info}</div> : null}

          {results.length ? (
            <div className="anm-avail">Available numbers</div>
          ) : null}
          <div className="anm-grid">
            {results.map((n) => (
              <div className="anm-item" key={n.phone_number}>
                <span className="anm-item-main">
                  <span className="anm-item-num">{n.phone_number}</span>
                  <span className="anm-item-meta">
                    {[n.locality, n.region].filter(Boolean).join(", ") ||
                      n.country}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-primary anm-buy"
                  onClick={() => buy(n.phone_number)}
                  disabled={buying !== null || n.sample}
                  title={n.sample ? "Sample row, not a real number" : undefined}
                >
                  {n.sample
                    ? "Sample"
                    : buying === n.phone_number
                      ? "Buying…"
                      : "Buy"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// The summary prompt's closing block ("Next step: ... / Data quality: ... /
// Outcome: ...") is written as three single-newline-separated lines, not
// three separate paragraphs — standard Markdown collapses a single newline
// inside a block into a space, so without this it rendered as one run-on
// paragraph instead of three distinct lines. Converts any single newline
// NOT already followed by a blank line into a hard line break (trailing
// double-space + newline); blank-line-separated paragraphs elsewhere in the
// summary are untouched, since a already-blank-line gap is left alone by
// the negative lookahead.
function withHardLineBreaks(text) {
  return text.replace(/([^\n])\n(?!\n)/g, "$1  \n")
}

// Once the call ends the queue and talking points are dead weight, so the rail becomes
// the AI call summary. The summary is written server-side after the recording is
// processed, so this shows a pending state and polls rather than inventing content.
function BdlSummaryRail({
  tokenInfo,
  peopleId,
  callSid,
  durationSec,
  connected,
  onRedial,
  redialDisabled
}) {
  const [summary, setSummary] = useState(null)
  const [status, setStatus] = useState(null)
  // Polling stopped without ever getting one. Both ceiling branches below used
  // to just return, which left the panel on the animated dots and "Writing the
  // summary from this call..." for good. A voicemail is the case that exposes
  // it: the call connects, so `connected` is true and a summary is promised,
  // but nobody spoke, so the backend produces nothing and never marks the
  // transcript failed either (ENG-3534).
  const [gaveUp, setGaveUp] = useState(false)
  const tenantId = tokenInfo?.tenant_id

  useEffect(() => {
    if (!tenantId || !peopleId || !connected) return
    let cancelled = false
    let timer = null
    const startedAt = Date.now()
    setGaveUp(false)

    const tick = () => {
      getDialerCallSummary(tokenInfo, peopleId, callSid)
        .then((res) => {
          if (cancelled) return
          const s = res?.data?.summary
          const st = res?.data?.status
          if (st) setStatus(st)
          if (s) {
            setSummary(s)
            return
          }
          // 'failed' is a known, permanent outcome (AssemblyAI exhausted its
          // retries server-side) — stop polling immediately instead of
          // continuing to check every SUM_POLL_MS until the ceiling, since
          // no future poll will change the answer.
          if (st === "failed") return
          if (Date.now() - startedAt >= SUM_POLL_CEILING_MS) {
            setGaveUp(true)
            return
          }
          timer = setTimeout(tick, SUM_POLL_MS)
        })
        .catch(() => {
          if (cancelled) return
          if (Date.now() - startedAt >= SUM_POLL_CEILING_MS) {
            setGaveUp(true)
            return
          }
          timer = setTimeout(tick, SUM_POLL_MS)
        })
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [tenantId, peopleId, callSid, connected])

  return (
    <div className="bdl-sumrail">
      <div className="bdl-sumrail-hd">
        {I.sparkle({ size: 12 })} AI call summary
        {onRedial && (
          <Tooltip title={redialDisabled ? "Line unavailable" : "Redial"}>
            {/* A disabled button fires no mouse events, so the tooltip hung off
                it never opened - and the disabled case is the one carrying the
                reason. The span is what the pointer meets instead. */}
            <span className="bdl-redial-wrap">
              <button
                className="bdl-redial is-ic bdl-sumrail-redial"
                onClick={onRedial}
                disabled={redialDisabled}
                aria-label="Redial"
                style={redialDisabled ? { pointerEvents: "none" } : undefined}
              >
                {I.rotateCcw({ size: 14 })}
              </button>
            </span>
          </Tooltip>
        )}
      </div>
      {!summary && !connected ? (
        <div className="bdl-sumpend">
          <p className="bdl-sumpend-t">No conversation to summarise</p>
        </div>
      ) : !summary && status === "failed" ? (
        <div className="bdl-sumpend bdl-sumfail">
          <p className="bdl-sumpend-t">
            {I.alert({ size: 12 })} Transcript couldn't be generated
          </p>
          <p className="bdl-sumpend-s">
            The call recording may have been too short or unclear to transcribe.
          </p>
        </div>
      ) : !summary && gaveUp ? (
        <div className="bdl-sumpend">
          <p className="bdl-sumpend-t">No summary for this call</p>
          <p className="bdl-sumpend-s">
            Nothing was said that could be summarised — a voicemail, or a call
            that ended before anyone spoke.
          </p>
        </div>
      ) : !summary ? (
        <div className="bdl-sumpend">
          <span className="bdl-sumdots">
            <i />
            <i />
            <i />
          </span>
          <p className="bdl-sumpend-t">Writing the summary from this call…</p>
        </div>
      ) : (
        <div className="bdl-sumbody">
          <div className="bdl-sumtag">{dlFmt(durationSec || 0)}</div>
          <MarkdownRender
            content={withHardLineBreaks(String(summary ?? ""))}
            className="bdl-sumtldr bdl-md"
          />
        </div>
      )}
    </div>
  )
}

function TalkingPointsPanel({
  points,
  generating,
  unavailable,
  open,
  onToggle,
  ringing
}) {
  if (!points.length && !generating && !unavailable && !ringing) return null

  // Generation is triggered server-side only when the callee picks up, so while
  // the phone is still ringing there is nothing being generated yet. Saying
  // "preparing" then would promise work that has not started; the pill and panel
  // say when it will happen instead.
  const label = points.length
    ? "Talking points"
    : unavailable
      ? "Talking points"
      : ringing
        ? "Talking points"
        : "Preparing talking points"

  // The pill reads left to right: the label was vertical, which made a short phrase
  // unreadable and the target awkward to hit.
  if (!open) {
    return (
      <button
        type="button"
        className="tkp-tab"
        onClick={onToggle}
        title="Show talking points"
        aria-expanded={false}
      >
        {I.sparkle({ size: 14 })}
        <span className="tkp-tab-tx">
          {label}
          {points.length ? ` · ${points.length}` : ""}
        </span>
      </button>
    )
  }

  return (
    <div className="tkp tkp-side is-open">
      <div className="tkp-tabs">
        <button
          type="button"
          className="tkp-tabbtn is-on"
          onClick={onToggle}
          aria-expanded
        >
          {I.sparkle({ size: 12 })} Talking points
          {points.length ? (
            <span className="tkp-n">{points.length}</span>
          ) : null}
        </button>
        <button
          type="button"
          className="tkp-hide"
          onClick={onToggle}
          title="Hide panel"
          aria-label="Hide talking points"
        >
          {I.chevR({ size: 14 })}
        </button>
      </div>
      {/* No "From your last call · updating…" banner. It reported plumbing
          rather than anything a rep can act on: the points on screen are the
          best available, and the moment better ones arrive they replace these
          on their own. Saying so mid-call only invited a pause to wonder
          whether to trust what was already there. `stale` still drives the
          refetch — it just no longer narrates it. */}
      {ringing && !points.length ? (
        <div className="tkp-none">
          Talking points are generated once the call is picked up.
        </div>
      ) : unavailable && !points.length ? (
        <div className="tkp-none">
          Couldn&apos;t generate talking points for this call.
        </div>
      ) : generating && !points.length ? (
        <TalkingPointsLoader />
      ) : null}
      <ol className="tkp-list">
        {points.map((p, i) => (
          <li key={p.slot || i}>
            <span className="tkp-num">{i + 1}</span>
            <span>
              <span className="tkp-pt-t">{p.title}</span>
              {p.detail && <span className="tkp-pt-d">{p.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function useHoldToggle(tokenInfo, callSidRef) {
  const [held, setHeld] = useState(false)
  const toggleHold = async () => {
    const next = !held
    setHeld(next)
    const sid = callSidRef.current
    if (!sid || !tokenInfo?.tenant_id) return
    try {
      await setCallHold(tokenInfo, sid, next)
    } catch {
      setHeld(!next)
    }
  }
  return [held, setHeld, toggleHold]
}

function dlInit(n) {
  return (n || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
}
// The callee's name in a call panel, with a LinkedIn glyph to its left that opens
// their profile. Shown wherever the dialer prints a name (compose, single call,
// bulk call); the icon is dropped when the person has no linkedin_url so the
// name still centres cleanly.
function DlrName({ name, linkedinUrl }) {
  return (
    <div className="dlr-name">
      {linkedinUrl ? (
        <a
          className="dlr-name-li"
          href={sanitizeUrl(linkedinUrl)}
          target="_blank"
          rel="noreferrer"
          title="Open LinkedIn profile"
          onClick={(e) => e.stopPropagation()}
        >
          {I.linkedin({ size: 14 })}
        </a>
      ) : null}
      <span>{name}</span>
    </div>
  )
}

// Turn a not-ready reason from the voice device into the call outcome the panels
// already know how to label, so a blocked mic or a stale line reads as an
// actionable message instead of the generic "calling line unavailable".
function dlNotReadyOutcome(reason) {
  if (reason === "mic") return "MIC_BLOCKED"
  if (reason === "token" || reason === "device") return "LINE_STALE"
  return "NO_LINE"
}
function dlOutcomeLabel(outcome) {
  switch (outcome) {
    case "NO_ANSWER":
      return "No answer"
    case "FAILED":
      return "Call failed"
    case "COMPLETED":
      return "Call ended"
    case "NO_LINE":
      return "Calling line unavailable"
    case "MIC_BLOCKED":
      return "Allow microphone access, then dial again"
    case "LINE_STALE":
      return "Reconnecting your line — dial again"
    case "NETWORK_LOST":
      return "Call dropped — connection lost"
    case "SESSION_ERROR":
      return "Couldn't start the session — please try again"
    default:
      return "Call ended"
  }
}

// The rep's calling line lives on one screen at a time: it is registered against
// their login, so opening the dialer elsewhere moves it and leaves this one
// holding a dead socket. Saying so beats a call that silently never dials.
const LINE_UNAVAILABLE_HINT =
  "Your calling line is open in another window or tab. Close it there, then reload this page."

function dlFmt(sec) {
  const m = Math.floor(sec / 60),
    s = sec % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

// The design's set, verbatim, and grouped as it groups them: what we reached versus what we did
// not. The previous list here was an older, unrelated set — meeting set, follow up, next call —
// which is why the panel disagreed with the design and with BDL_OUTCOMES elsewhere in this file.
// Exported so the in-row panel offers the SAME outcomes as the floating
// dialer. A second list here is exactly how the two drifted apart before (see
// the note above).
export const CALL_DISPO = [
  {
    id: "connected",
    label: "Connected",
    icon: "checkCircle",
    cls: "follow",
    grp: "reached"
  },
  {
    id: "meeting",
    label: "Meeting Booked",
    icon: "calendar",
    cls: "meet",
    grp: "reached"
  },
  {
    id: "livemsg",
    label: "Left message",
    icon: "reply",
    cls: "reach",
    grp: "reached"
  },
  {
    id: "voicemail",
    label: "Left voicemail",
    icon: "mic",
    cls: "next",
    grp: "reached"
  },
  { id: "busy", label: "Busy", icon: "clock", cls: "none", grp: "missed" },
  {
    id: "noanswer",
    label: "No answer",
    icon: "phoneOff",
    cls: "none",
    grp: "missed"
  },
  {
    id: "wrongnum",
    label: "Wrong number",
    icon: "close",
    cls: "none",
    grp: "missed"
  }
]
const CALL_DISPO_LABEL = Object.fromEntries(
  CALL_DISPO.map((d) => [d.id, d.label])
)
const CALL_DISPO_CLS = Object.fromEntries(CALL_DISPO.map((d) => [d.id, d.cls]))
function clkFmtMeeting(at) {
  if (!at || !at.date) return ""
  const d = new Date(at.date + "T" + (at.time || "09:00"))
  const day = d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric"
  })
  return at.time ? day + " · " + at.time : day
}

// Anything the rep can click, type in or scroll starts an interaction, not a drag.
// The header is NOT listed: it is the drag handle now.
const NO_DRAG =
  "button, a, input, textarea, select, label, [role=button], [contenteditable]"
// The panel moves by its header, the way a window does.
//
// It used to be grabbed from a 14px band around the frame, which put the move
// cursor on the panel's edge — an odd place to reach for, and it meant the
// header showed a move cursor for a drag that started somewhere else. One
// handle, in the obvious place, and the cursor only changes over it.
// The full panels drag by their header. The minidock has none — it is a compact
// strip of avatar, status and buttons — so the dock itself is the handle, with
// its buttons excluded by NO_DRAG.
const DRAG_HANDLE = ".dlr-hd, .bdl-hd, .bdl-minidock"

// A number typed without a country code is dialled against the carrier's default,
// which is why 9887946797 reached nobody and +919887946797 connected. The rep is
// asked for the country code rather than us guessing one on their behalf.
// Fifteen characters in total, counting the plus and any separators.
const DIAL_MAXLEN = 15
const DTMF_MAXLEN = 20
function pushDigit(prev, key) {
  return (prev + key).slice(-DTMF_MAXLEN)
}
// However far the panel is dragged, this much of it stays inside the viewport, so it can
// always be grabbed again and its close button can always be reached.
const DRAG_KEEP_VISIBLE = 80

function useDragOffset() {
  const [off, setOff] = useState({ x: 0, y: 0 })
  const [onEdge, setOnEdge] = useState(false)
  const start = useRef(null)
  const nodeRef = useRef(null)

  // True when the pointer is over the header and not over something inside it
  // that the rep means to click (the close button lives there).
  const onHandle = (e) =>
    Boolean(e.target?.closest?.(DRAG_HANDLE)) && !e.target?.closest?.(NO_DRAG)

  const clamp = (x, y) => {
    const box = nodeRef.current?.getBoundingClientRect()
    if (!box) return { x, y }
    const left = box.left - off.x
    const top = box.top - off.y
    const minX = DRAG_KEEP_VISIBLE - left - box.width
    const maxX = window.innerWidth - left - DRAG_KEEP_VISIBLE
    const minY = -top
    const maxY = window.innerHeight - top - DRAG_KEEP_VISIBLE
    return {
      x: Math.min(Math.max(x, minX), maxX),
      y: Math.min(Math.max(y, minY), maxY)
    }
  }

  const onDragStart = (e) => {
    if (e.button !== 0) return
    if (!onHandle(e)) return
    e.stopPropagation()
    start.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y }
    const move = (ev) => {
      ev.preventDefault()
      setOff(
        clamp(
          start.current.ox + ev.clientX - start.current.sx,
          start.current.oy + ev.clientY - start.current.sy
        )
      )
    }
    const up = () => {
      window.removeEventListener("mousemove", move)
      window.removeEventListener("mouseup", up)
      document.body.style.userSelect = ""
    }
    document.body.style.userSelect = "none"
    window.addEventListener("mousemove", move)
    window.addEventListener("mouseup", up)
  }

  // A window the panel used to fit in can be resized out from under it.
  useEffect(() => {
    const onResize = () => setOff((o) => clamp(o.x, o.y))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  })

  return {
    ref: nodeRef,
    // Marks the frames that actually move. The setup and recap frames reuse the
    // same shell classes without the handlers, and were showing a drag frame and
    // a move cursor for a drag that could never happen.
    className: " is-draggable" + (onEdge ? " is-drag-handle" : ""),
    style: { transform: `translate(${off.x}px, ${off.y}px)` },
    onDragStart,
    onMouseMove: (e) => {
      const next = onHandle(e)
      if (next !== onEdge) setOnEdge(next)
    },
    onMouseLeave: () => setOnEdge(false),
    reset: () => setOff({ x: 0, y: 0 })
  }
}

function CallLock4L({
  person,
  durationSec,
  outcome,
  log,
  setLog,
  onRedial,
  variant = "wide"
}) {
  const preset = (days, time) => {
    const d = new Date()
    d.setDate(d.getDate() + days)
    const p = (n) => String(n).padStart(2, "0")
    setLog((l) => ({
      ...l,
      meetingAt: {
        date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
        time
      }
    }))
  }
  const mAt = log.meetingAt || {}
  return (
    <div className={"clk clk-" + variant}>
      <div className="clk-hd">
        <span className="dlr-av sm">{dlInit(person.name)}</span>
        <div className="clk-id">
          <span className="clk-nm">{person.name}</span>
          <span className="clk-meta">
            {I.checkCircle({ size: 11 })}{" "}
            {!outcome
              ? "Call ended"
              : outcome !== "COMPLETED" && outcome !== "ANSWERED"
                ? dlOutcomeLabel(outcome)
                : "Connected · " + dlFmt(durationSec)}
          </span>
        </div>
        {onRedial && (
          <Tooltip title="Redial">
            <button
              className="bdl-redial is-ic"
              onClick={onRedial}
              aria-label="Redial"
            >
              {I.rotateCcw({ size: 14 })}
            </button>
          </Tooltip>
        )}
      </div>
      <div className="clk-cols">
        <div className="clk-form">
          <div className="clk-label">Log the call — what happened?</div>
          {/* Two grids, as the design has it: what we reached, then what we did not. They are
              sized differently, so a single flat list cannot lay out correctly. */}
          {["reached", "missed"].map((grp) => (
            <div
              key={grp}
              className={
                "clk-dispo" + (grp === "missed" ? " clk-dispo-missed" : "")
              }
            >
              {CALL_DISPO.filter((o) => o.grp === grp).map((o) => (
                <button
                  key={o.id}
                  className={
                    "clk-dbtn is-" +
                    o.cls +
                    (log.dispo === o.id ? " is-on" : "")
                  }
                  onClick={() =>
                    setLog((l) => ({
                      ...l,
                      dispo: l.dispo === o.id ? "" : o.id
                    }))
                  }
                >
                  {I[o.icon]({ size: 14 })} {o.label}
                </button>
              ))}
            </div>
          ))}
          {log.dispo === "meeting" && (
            <div className="clk-meeting">
              <div className="clk-meeting-t">
                {I.calendar({ size: 13 })} When's the meeting?
              </div>
              <div className="clk-meeting-row">
                <input
                  className="clk-dt"
                  type="date"
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  value={mAt.date || ""}
                  onChange={(e) =>
                    setLog((l) => ({
                      ...l,
                      meetingAt: { ...l.meetingAt, date: e.target.value }
                    }))
                  }
                />
                <input
                  className="clk-dt"
                  type="time"
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  value={mAt.time || ""}
                  onChange={(e) =>
                    setLog((l) => ({
                      ...l,
                      meetingAt: { ...l.meetingAt, time: e.target.value }
                    }))
                  }
                />
              </div>
              <div className="clk-presets">
                <button
                  className="clk-preset"
                  onClick={() => preset(1, "10:00")}
                >
                  Tomorrow · 10:00
                </button>
                <button
                  className="clk-preset"
                  onClick={() => preset(2, "14:00")}
                >
                  In 2 days · 14:00
                </button>
                <button
                  className="clk-preset"
                  onClick={() => preset(7, "11:00")}
                >
                  Next week · 11:00
                </button>
              </div>
              {mAt.date && (
                <div className="clk-meeting-confirm">
                  {I.check({ size: 12 })} Meeting set for{" "}
                  <b>{clkFmtMeeting(mAt)}</b>
                </div>
              )}
            </div>
          )}
          <div className="clk-label clk-label-mt">Notes</div>
          <textarea
            className="bdl-notes"
            rows={variant === "compact" ? 2 : 3}
            placeholder="How did the call go? Add your notes here - outcomes, objections, next steps, and more"
            value={log.notes}
            onChange={(e) => setLog((l) => ({ ...l, notes: e.target.value }))}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * @param {{
 *   person: any,
 *   rep?: any,
 *   onClose?: any,
 *   onLogCall?: any,
 *   inbound?: { person: any, call: any } | null
 * }} props
 */
export function Dialer4L({
  person,
  rep = DEMO_REP,
  onClose,
  onLogCall,
  inbound
}) {
  // The same Boost Connect settings the bulk session dials with. Single and
  // ad-hoc calls used to pass calleeCountry unconditionally and never pass
  // rotateAfter at all, so turning country matching off did nothing here and
  // rotation applied only inside a bulk session.
  const callerConfig = useVoiceSessionStore((s) => s.callerConfig)
  // Typing a number is not a person, so the panel drops the identity block and
  // the "no number on file" warning: there was never a number to be missing.
  const adHoc = person.id === "ad-hoc"
  const onFile = person.phone && /\d/.test(person.phone) ? person.phone : ""
  // An answered callback is already connected, so there is nothing to compose or dial:
  // the panel opens on the live call the rep just picked up.
  const [phase, setPhase] = useState(inbound ? "live" : "compose")
  const [number, setNumber] = useState(onFile)
  const [dialCode, setDialCode] = useState("+1")
  const [dialCodePicked, setDialCodePicked] = useState(false)
  const [sec, setSec] = useState(0)
  const [muted, setMuted] = useState(false)
  const drag = useDragOffset()
  const [showPad, setShowPad] = useState(false)
  const [pad, setPad] = useState("")
  const tRef = useRef(null)
  const {
    status: devStatus,
    notReadyRef,
    reconnecting,
    networkDropped,
    clearNetworkDropped,
    makeCall,
    logCall,
    watchCallStream,
    sendDigits,
    endSession
  } = useVoiceDevice()
  const [outcome, setOutcome] = useState(null)
  const [aiSummary, setAiSummary] = useState(null)
  const [summaryFailed, setSummaryFailed] = useState(false)
  const callRef = useRef(null)
  const callSidRef = useRef(null)
  const stopWatchingRef = useRef(() => {})
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const [held, , toggleHold] = useHoldToggle(tokenInfo, callSidRef)
  const [tpOpen, setTpOpen] = useState(true)
  // Anything generated before THIS call began belongs to the previous one, so
  // the dial time is the freshness cut-off. Passing 0 here - which is what this
  // did - made `isStale` unreachable and the "From your last call" banner dead
  // code: an eight-day-old record rendered while the phone was still ringing,
  // presented as if it had been written for the call in progress. Generation is
  // server-side on pickup, so during ringing stored points are ALWAYS the
  // previous call's.
  const [dialStartedAt, setDialStartedAt] = useState(0)
  useEffect(() => {
    if (phase === "ringing") setDialStartedAt(Date.now())
  }, [phase])
  const manualPoints = useDialerTalkingPoints(
    tokenInfo,
    person?.id,
    phase === "ringing" || phase === "live",
    dialStartedAt,
    phase === "live"
  )
  useEffect(() => {
    callRef.current?.mute(muted)
  }, [muted])
  const [showProfile, setShowProfile] = useState(false)
  const [lock, setLock] = useState({ dispo: "", meetingAt: null, notes: "" })
  // Connecting on a number can change what that number IS for this rep — the
  // service records it against the person. The list behind the dialer would keep
  // showing the old one until something told it otherwise.
  const qc = useQueryClient()
  // The digits field is always live now (no separate "on file" read-only
  // view), so whatever it holds — pre-filled from the person or hand-typed —
  // has to actually validate as dialable rather than trusting onFile blindly.
  const dialable = isDialable(number)

  // number stays the single +code+digits string dialing/validation already
  // relies on. dialCode only drives the dropdown, so it has to re-derive from
  // number whenever something OTHER than the dropdown sets it (a contact's
  // number, the number on file, a reset) — otherwise the dropdown shows the
  // last code picked while the digits field is silently missing the actual
  // prefix that number carries.
  useEffect(() => {
    // Longest code first: a couple of country codes are one another's
    // prefix (e.g. +1 vs the +1 NANP members share it exactly, but +7 vs a
    // future 3-digit code starting 7 would not), so the more specific match
    // has to win over a shorter accidental one.
    const match = [...DIAL_CODES]
      .sort((a, b) => b.value.length - a.value.length)
      .find((c) => number.startsWith(c.value))
    if (match && match.value !== dialCode) setDialCode(match.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number])

  const localDigits = number.startsWith(dialCode)
    ? number.slice(dialCode.length)
    : number.replace(/^\+\d*/, "")

  // Built once, not per-keystroke: DIAL_CODES itself never changes, only
  // which entry the rep has picked or is filtering toward.
  const codeOptions = useMemo(() => dialCodeOptions(), [])

  useEffect(() => {
    if (!networkDropped) return
    setOutcome("NETWORK_LOST")
    setPhase("ended")
    clearNetworkDropped()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkDropped])

  useEffect(() => {
    if (phase !== "ringing") return
    setOutcome(null)
    setAiSummary(null)
    let cancelled = false
    ;(async () => {
      const call =
        devStatus === "ready"
          ? await makeCall(
              number,
              (() => {
                const params = {}
                if (callerConfig.countryMatch) {
                  // A typed number has no location_country of its own — the
                  // country dropdown next to it is the only signal we have
                  // for which one to match a caller number against, so it
                  // stands in for the person's location on a manual dial.
                  // Only when the picked code maps to exactly one country:
                  // +1 alone covers 25 (US, Canada, the Caribbean), and a
                  // wrong guess there is worse than sending no country at all.
                  const codeIsos = DIAL_CODES.find(
                    (c) => c.value === dialCode
                  )?.isos
                  const dialCodeCountry =
                    codeIsos?.length === 1 ? codeIsos[0] : undefined
                  const calleeCountry =
                    person?.location_country || dialCodeCountry
                  if (calleeCountry) params.calleeCountry = calleeCountry
                }
                if (callerConfig.rotation && callerConfig.rotateN)
                  params.rotateAfter = String(callerConfig.rotateN)
                if (person?.listId) params.list_id = person.listId
                return Object.keys(params).length ? params : undefined
              })(),
              person?.id,
              undefined,
              person?.linkedin_url
            )
          : null
      if (cancelled) return
      if (!call) {
        setOutcome(dlNotReadyOutcome(notReadyRef.current))
        setPhase("ended")
        return
      }
      callRef.current = call
      call.on("accept", () => {
        callSidRef.current = call.parameters?.CallSid ?? null
        setAiSummary(null)
        setSummaryFailed(false)
        qc.invalidateQueries({ queryKey: ["people-os"] })
        stopWatchingRef.current = watchCallStream(callSidRef.current, {
          onStatus: (status) => {
            setOutcome(status)
            if (status === "ANSWERED") setPhase("live")
          },
          onSummary: setAiSummary,
          onFailed: () => setSummaryFailed(true)
        })
      })
      const end = () => {
        clearInterval(tRef.current)
        callRef.current = null
        setPhase("ended")
      }
      call.on("disconnect", end)
      call.on("cancel", end)
      call.on("reject", end)
      call.on("error", end)
    })()
    return () => {
      cancelled = true
    }
  }, [phase, devStatus, makeCall, number, watchCallStream])
  useEffect(() => {
    return () => stopWatchingRef.current()
  }, [])
  // The inbound call was accepted by the toast, not placed here, so its handles are adopted
  // rather than created and the panel watches it for its summary like any other call.
  useEffect(() => {
    if (!inbound?.call) return
    const call = inbound.call
    callRef.current = call
    callSidRef.current = call.parameters?.CallSid ?? null
    if (callSidRef.current) {
      stopWatchingRef.current = watchCallStream(callSidRef.current, {
        onStatus: setOutcome,
        onSummary: setAiSummary,
        onFailed: () => setSummaryFailed(true)
      })
    }
    const end = () => {
      clearInterval(tRef.current)
      callRef.current = null
      setPhase("ended")
    }
    call.on("disconnect", end)
    call.on("cancel", end)
    call.on("reject", end)
    call.on("error", end)
  }, [inbound, watchCallStream])
  useEffect(() => {
    if (adHoc || !person?.id) return
    if (phase !== "ringing" && phase !== "live") return
    if (!tokenInfo) return
    let cancelled = false
    const startedAt = Date.now()
    const tick = async () => {
      try {
        const res = await getDialerCallHistory(tokenInfo, {
          tab: "all",
          personIds: [person.id]
        })
        if (cancelled) return
        const calls = res?.data?.data?.calls ?? []
        const row = calls.find((c) => {
          const t = Date.parse(c.created_at ?? "")
          return Number.isFinite(t) && t >= startedAt - 60000
        })
        if (!row) return
        if (row.twilio_call_sid && !callSidRef.current) {
          callSidRef.current = row.twilio_call_sid
        }
        if (row.status) setOutcome(row.status)
        if (row.status === "ANSWERED") setPhase("live")
        else if (DL_TERMINAL_STATUSES.includes(row.status)) setPhase("ended")
      } catch {
        return
      }
    }
    tick()
    const id = setInterval(tick, 2000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [phase, adHoc, person?.id, tokenInfo])

  useEffect(() => {
    if (phase !== "live") return
    tRef.current = setInterval(() => setSec((s) => s + 1), 1000)
    return () => clearInterval(tRef.current)
  }, [phase])

  const hangUp = () => {
    clearInterval(tRef.current)
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    setPhase("ended")
  }
  const redial = () => {
    clearInterval(tRef.current)
    stopWatchingRef.current()
    stopWatchingRef.current = () => {}
    callSidRef.current = null
    setSec(0)
    setOutcome(null)
    setAiSummary(null)
    setSummaryFailed(false)
    setMuted(false)
    setShowPad(false)
    setPad("")
    setLock({ dispo: "", meetingAt: null, notes: "" })
    setPhase("ringing")
  }
  const saveClose = () => {
    const dur = Math.max(sec, 1)
    logCall(callSidRef.current, {
      disposition: lock.dispo || undefined,
      notes: lock.notes || undefined,
      meetingAt: lock.meetingAt
    })
    endSession()
    if (onLogCall)
      onLogCall({
        id: "call-" + Date.now(),
        type: "call",
        dir: "out",
        outcome: "connected",
        durationSec: dur,
        duration: dlFmt(dur),
        when: "Just now",
        date: "Today",
        rep,
        disposition: lock.dispo,
        meetingAt: lock.meetingAt,
        notes: lock.notes,
        summary: { tldr: aiSummary, failed: summaryFailed }
      })
    if (onClose) onClose()
  }

  // ENG-3524. The X used to call onClose straight through, and nothing in this
  // component hangs up on unmount - the only disconnect() lives in hangUp().
  // Closing the window mid-call therefore took the UI away and left the leg up:
  // the prospect still connected, Twilio still billing, and no way back to it.
  // BulkDialer4L has always ended its call before closing; this is the same
  // thing for the single-call panel.
  const closePanel = () => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    clearInterval(tRef.current)
    stopWatchingRef.current()
    stopWatchingRef.current = () => {}
    if (onClose) onClose()
  }

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"]
  const sub = {
    2: "ABC",
    3: "DEF",
    4: "GHI",
    5: "JKL",
    6: "MNO",
    7: "PQRS",
    8: "TUV",
    9: "WXYZ",
    0: "+"
  }

  // Talking points earn their space only once they exist AND the call is up.
  //
  // Two things were wrong. The panel rendered from the moment the phone started
  // ringing, so a call nobody had answered yet opened at double width around an
  // empty column. And collapsing it put the .tkp-tab pill inside a stage that is
  // flex-column by default, where a pill with no intrinsic width stretches edge
  // to edge - the fat "TALKING POINTS - 2" bar above the avatar, occupying a
  // third of the panel to say nothing.
  //
  // tpReady makes the stage a ROW whenever there is something to show, so the
  // collapsed pill is a narrow rail beside the call rather than a banner over
  // it. tpSplit is what actually doubles the panel, and only for the open state.
  const tpReady = phase === "live" && manualPoints.points.length > 0
  const tpSplit = tpReady && tpOpen

  return (
    <React.Fragment>
      <div
        className={
          "dlr" +
          drag.className +
          (tpSplit || phase === "ended" ? " is-split" : "")
        }
        ref={drag.ref}
        style={drag.style}
        role="dialog"
        aria-label="Call"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={drag.onDragStart}
        onMouseMove={drag.onMouseMove}
        onMouseLeave={drag.onMouseLeave}
      >
        <div className="dlr-hd">
          <span className="dlr-hd-t">
            {I.phone({ size: 13 })}{" "}
            {phase === "ended"
              ? dlOutcomeLabel(outcome)
              : phase === "live"
                ? "On call"
                : phase === "ringing"
                  ? "Calling…"
                  : adHoc
                    ? "Call a number"
                    : "New call"}
          </span>
          <span className="dlr-hd-sp" />
          <button
            className="dlr-x"
            onClick={closePanel}
            aria-label="Close dialer"
          >
            {I.close({ size: 15 })}
          </button>
        </div>

        {phase === "compose" && (
          <div className={"dlr-stage dlr-compose" + (adHoc ? " is-adhoc" : "")}>
            {adHoc ? null : (
              <>
                <span className="dlr-av">{dlInit(person.name)}</span>
                <DlrName name={person.name} linkedinUrl={person.linkedin_url} />
                <div className="dlr-role">
                  {person.title}
                  {person.account ? " · " + person.account.name : ""}
                </div>
              </>
            )}
            {person.linkedin_url ? (
              <button
                className="bdl-viewprofile"
                onClick={() => setShowProfile(true)}
              >
                {I.user({ size: 13 })} Pre-meeting brief
              </button>
            ) : null}

            <div className="dlr-numedit">
              <label className="dlr-numedit-lbl">
                {adHoc ? (
                  "Enter a number with its country code"
                ) : onFile ? (
                  person.contact_phone && person.contact_phone !== onFile ? (
                    "Number you last reached them on"
                  ) : (
                    "Number on file"
                  )
                ) : (
                  <>
                    <span className="dlr-nonum">
                      {I.info({ size: 12 })}
                      {person.phone_lookup === "not_found"
                        ? " We could not find a number for this person"
                        : " No number on file"}
                    </span>
                    <span className="dlr-nonum-hint">
                      Enter the number you&apos;d like to dial along with the
                      country code
                    </span>
                  </>
                )}
              </label>
              <div className="dlr-numinput-row">
                <Select
                  className={
                    "dlr-codeselect" +
                    (localDigits || dialCodePicked
                      ? " dlr-codeselect-active"
                      : "")
                  }
                  value={dialCode}
                  onChange={(nextCode) => {
                    setDialCode(nextCode)
                    setDialCodePicked(true)
                    setNumber(`${nextCode}${localDigits}`)
                  }}
                  showSearch
                  variant="outlined"
                  popupMatchSelectWidth={140}
                  placement="bottomLeft"
                  // Default document.body portal, antd's own trigger
                  // positioning engine — a getPopupContainer + manual CSS
                  // centering attempt fought that engine's own inline
                  // positioning styles and made the popup land in the
                  // wrong place. Left-aligned directly below the code box
                  // is what actually renders correctly.
                  popupClassName="dlr-codeselect-popup"
                  // antd swaps its default suffix icon to a magnifying
                  // glass the moment showSearch is open — fine on a full
                  // search box, but on this narrow 108px field it read as
                  // a second control fighting the chevron for space. Pins
                  // it to one chevron open or closed, same as every other
                  // dropdown affordance already in this panel.
                  suffixIcon={I.chevD({ size: 14 })}
                  aria-label="Country code"
                  options={codeOptions}
                  filterOption={(input, option) =>
                    option?.value.toLowerCase().includes(input.toLowerCase()) ||
                    option?.isos?.some((iso) =>
                      iso.toLowerCase().includes(input.toLowerCase())
                    )
                  }
                />
                <input
                  className="dlr-numinput"
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  maxLength={DIAL_MAXLEN}
                  placeholder="415 555 0100"
                  value={localDigits}
                  // Digits only — the country code has its own dropdown
                  // now, so this field has no reason to accept
                  // spaces/parens/hyphens the way a free-typed
                  // international number used to need.
                  onChange={(e) =>
                    setNumber(`${dialCode}${e.target.value.replace(/\D/g, "")}`)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && dialable) setPhase("ringing")
                  }}
                />
              </div>
              {person.contact_phone && person.contact_phone !== person.phone ? (
                <button
                  className="dlr-numswap"
                  onClick={() => setNumber(person.contact_phone)}
                >
                  Use the number on the contact ({person.contact_phone})
                </button>
              ) : null}
              {onFile && number !== onFile && (
                <button
                  className="dlr-numswap"
                  onClick={() => setNumber(onFile)}
                >
                  Use number on file · {onFile}
                </button>
              )}
            </div>

            {devStatus !== "ready" && (
              <div className="dlr-noline">
                {I.info({ size: 12 })} {LINE_UNAVAILABLE_HINT}
              </div>
            )}
            <button
              className="dlr-callbtn"
              disabled={!dialable || devStatus !== "ready"}
              onClick={() => setPhase("ringing")}
            >
              {I.phone({ size: 17 })} Call
            </button>
          </div>
        )}

        {(phase === "ringing" || phase === "live") && (
          <div className={"dlr-stage" + (tpReady ? " dlr-stage-split" : "")}>
            {tpReady ? (
              <TalkingPointsPanel
                points={manualPoints.points}
                generating={manualPoints.generating}
                unavailable={manualPoints.unavailable}
                open={tpOpen}
                ringing={phase === "ringing"}
                onToggle={() => setTpOpen((o) => !o)}
              />
            ) : null}
            <div className="dlr-callcol">
              <span
                className={"dlr-av" + (phase === "ringing" ? " is-ring" : "")}
              >
                {dlInit(person.name)}
              </span>
              <DlrName name={person.name} linkedinUrl={person.linkedin_url} />
              <div className="dlr-role">{person.title}</div>
              {person.linkedin_url ? (
                <button
                  className="bdl-viewprofile"
                  onClick={() => setShowProfile(true)}
                >
                  {I.user({ size: 13 })} Pre-meeting brief
                </button>
              ) : null}
              <div className="dlr-num">{number}</div>
              {pad ? <div className="dlr-dtmf">{pad}</div> : null}
              <div className={"dlr-status is-" + phase}>
                {reconnecting
                  ? "Reconnecting…"
                  : phase === "live"
                    ? dlFmt(sec)
                    : "Ringing…"}
              </div>
              {reconnecting && (
                <div className="dlr-netdrop">
                  {I.info({ size: 12 })} Connection lost. Trying to get the call
                  back — it will end if this does not clear.
                </div>
              )}

              {showPad && (
                <div className="dlr-pad">
                  {keys.map((k) => (
                    <button
                      className="dlr-key"
                      key={k}
                      onClick={() => {
                        setPad((p) => pushDigit(p, k))
                        sendDigits(k)
                      }}
                    >
                      <span className="dlr-key-n">{k}</span>
                      <span className="dlr-key-s">{sub[k] || " "}</span>
                    </button>
                  ))}
                  <button
                    className="dlr-padback"
                    onClick={() => setPad((p) => p.slice(0, -1))}
                    disabled={!pad}
                  >
                    {I.backspace({ size: 15 })} Delete
                  </button>
                </div>
              )}

              <div className="dlr-controls">
                <button
                  className={"dlr-ctl" + (muted ? " is-on" : "")}
                  onClick={() => setMuted((m) => !m)}
                >
                  {muted ? I.micOff({ size: 18 }) : I.mic({ size: 18 })}
                  <span>{muted ? "Unmute" : "Mute"}</span>
                </button>
                <button
                  className={"dlr-ctl" + (showPad ? " is-on" : "")}
                  onClick={() => setShowPad((s) => !s)}
                >
                  {I.keypad({ size: 18 })}
                  <span>Keypad</span>
                </button>
                <button
                  className={"dlr-ctl" + (held ? " is-on" : "")}
                  onClick={toggleHold}
                >
                  {held ? I.pauseOff({ size: 18 }) : I.pause({ size: 18 })}
                  <span>Hold</span>
                </button>
              </div>

              <button
                className="dlr-hang"
                onClick={hangUp}
                aria-label="End call"
              >
                {I.phoneOff({ size: 22 })}
              </button>
            </div>
          </div>
        )}

        {phase === "ended" && (
          <div className="dlr-lockwrap dlr-lockwrap-split">
            <BdlSummaryRail
              tokenInfo={tokenInfo}
              peopleId={person?.id}
              callSid={callSidRef.current}
              durationSec={sec}
              connected={sec > 0}
              onRedial={redial}
              redialDisabled={!dialable || devStatus !== "ready"}
            />
            <div className="dlr-lockcol">
              <CallLock4L
                person={person}
                durationSec={sec}
                outcome={outcome}
                log={lock}
                setLog={setLock}
                variant="compact"
              />
              <div className="dlr-lockfoot">
                <button
                  className="dlr-callbtn dlr-lockbtn"
                  disabled={!lock.dispo}
                  onClick={saveClose}
                >
                  {I.check({ size: 15 })} Save &amp; close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showProfile && (
        <PersonBriefHost
          person={person}
          onClose={() => setShowProfile(false)}
        />
      )}
    </React.Fragment>
  )
}

const BDL_OUTCOMES = [
  { id: "connected", label: "Connected", icon: "checkCircle" },
  { id: "meeting", label: "Meeting booked", icon: "calendar" },
  { id: "livemsg", label: "Left message", icon: "reply" },
  { id: "voicemail", label: "Left voicemail", icon: "mic" },
  { id: "busy", label: "Busy", icon: "clock" },
  { id: "noanswer", label: "No answer", icon: "phoneOff" },
  { id: "wrongnum", label: "Wrong number", icon: "close" },
  { id: "skipped", label: "Skipped", icon: "chevR" }
]
// Dialed counts every contact we actually put a call to. Reached counts only the ones
// where a human picked up — voicemail and busy are not people we spoke to, and folding
// them into the same number is what made the connect rate read high.
const BDL_DONE_STATUS = [
  "connected",
  "meeting",
  "livemsg",
  "voicemail",
  "noanswer",
  "busy",
  "wrongnum"
]
const BDL_REACHED_STATUS = ["connected", "meeting", "livemsg"]
const BDL_STLABEL = {
  queued: "Queued",
  connected: "Connected",
  voicemail: "Voicemail",
  noanswer: "No answer",
  skipped: "Skipped"
}
// Timezone buckets the queue can be filtered by, at the granularity we actually
// know a contact's location: their country. [key, label, caller-country].
const BDL_TZ = [
  ["US", "United States", "US"],
  ["EU", "UK / Europe", "GB"],
  ["IN", "India", "IN"],
  ["SG", "Singapore", "SG"]
]
const BDL_TZ_BY_COUNTRY = {
  US: "US",
  USA: "US",
  "UNITED STATES": "US",
  "UNITED STATES OF AMERICA": "US",
  GB: "EU",
  UK: "EU",
  "UNITED KINGDOM": "EU",
  DE: "EU",
  GERMANY: "EU",
  FR: "EU",
  FRANCE: "EU",
  NL: "EU",
  NETHERLANDS: "EU",
  IE: "EU",
  IRELAND: "EU",
  ES: "EU",
  SPAIN: "EU",
  IT: "EU",
  ITALY: "EU",
  IN: "IN",
  INDIA: "IN",
  SG: "SG",
  SINGAPORE: "SG"
}
// The contact's timezone bucket, or null when their location is unknown. A null
// is never filtered out: a missing location must not silently drop someone from
// the queue — we dial them anyway and let the rep decide.
function tzFromPerson(p) {
  const c = String(p?.location_country || p?.country || "")
    .trim()
    .toUpperCase()
  if (!c) return null
  return BDL_TZ_BY_COUNTRY[c] || null
}
function bdlCC(tz) {
  const row = BDL_TZ.find((t) => t[0] === tz)
  return row ? row[2] : "US"
}
export function BulkDialer4L({
  people,
  rep = DEMO_REP,
  onClose,
  onLogCall,
  onEnrich
}) {
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  // Rotation and country matching come from the shared store, so the settings
  // the rep chose in "Manage call from numbers" are the ones this session
  // dials with. They used to be local state seeded to defaults on every mount,
  // which meant the panel here and the rail could disagree and neither
  // survived a close. `numbers` stays local: it is the fetched pool, not config.
  const callerConfig = useVoiceSessionStore((s) => s.callerConfig)
  const setCallerConfig = useVoiceSessionStore((s) => s.setCallerConfig)
  const [poolNumbers, setPoolNumbers] = useState([])
  const dialerCfg = { ...callerConfig, numbers: poolNumbers }
  const setDialerCfg = (updater) => {
    const next = typeof updater === "function" ? updater(dialerCfg) : updater
    if (next.numbers !== poolNumbers) setPoolNumbers(next.numbers)
    const { rotation, rotateN, countryMatch } = next
    setCallerConfig({ rotation, rotateN, countryMatch })
  }
  const persistCfg = (next) => setDialerCfg(next)

  useEffect(() => {
    if (!tokenInfo?.tenant_id) return
    let cancelled = false
    getVoiceNumbers(tokenInfo)
      .then((res) => {
        if (cancelled) return
        const pool = (res?.data?.numbers || []).map((n) => ({
          id: n.phone_number,
          number: n.phone_number,
          region: COUNTRY_LABEL[n.country_code] || n.country_code || "—",
          cc: n.country_code || "—",
          calls: n.day_count || 0,
          connects: n.answer_rate ?? null,
          spam: n.status === "retired"
        }))
        if (pool.length) setPoolNumbers(pool)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [tokenInfo?.tenant_id])
  const patchCfg = (p) => persistCfg({ ...dialerCfg, ...p })
  const toggleSpam = (id) =>
    persistCfg({
      ...dialerCfg,
      numbers: dialerCfg.numbers.map((n) =>
        n.id === id ? { ...n, spam: !n.spam } : n
      )
    })
  const removeNum = (id) =>
    persistCfg({
      ...dialerCfg,
      numbers: dialerCfg.numbers.filter((n) => n.id !== id)
    })

  const [queue, setQueue] = useState(() =>
    people.map((p) => ({
      id: p.id,
      queueId: null,
      person: p,
      number: p.phone && /\d/.test(p.phone) ? p.phone : "",
      tz: tzFromPerson(p),
      status: "queued",
      durationSec: 0,
      dispo: "",
      meetingAt: null,
      notes: "",
      summary: null
    }))
  )
  const [order, setOrder] = useState([])
  // goTo is called from several effects (line ~1684, ~1770, ~1944, ~1997,
  // ~2876) that don't pass `order` explicitly, relying on goTo's `ord = order`
  // default parameter instead. That default closes over whatever `order` was
  // when THAT specific effect's closure was created — if the effect hasn't
  // re-run since order last changed (e.g. startSession just called
  // setOrder(ord) with 5 entries), a still-live older closure can call
  // goTo(oi + 1) with a stale, shorter order baked in, satisfying the
  // oi+1 >= ord.length completion check early. A ref is never stale — every
  // read sees the value from the most recent setOrder, regardless of which
  // render's closure is doing the reading.
  const orderRef = useRef([])
  useEffect(() => {
    orderRef.current = order
  }, [order])
  const [oi, setOi] = useState(0)
  const [stage, setStage] = useState("setup")
  const [railOpen, setRailOpen] = useState(true)
  const [leftTab, setLeftTab] = useState("queue")
  const [numbersOpen, setNumbersOpen] = useState(false)
  const [hoursOpen, setHoursOpen] = useState(false)
  const [tzOpen, setTzOpen] = useState(false)
  const [addNumOpen, setAddNumOpen] = useState(false)
  const [sessionMin, setSessionMin] = useState(false)
  const [miniTalk, setMiniTalk] = useState(false)
  const [liveAt, setLiveAt] = useState(0)
  const [resuming, setResuming] = useState(true)
  // Disables the Start button the instant it's clicked, synchronously,
  // before React can dispatch a second click event for the same
  // interaction. startingSessionRef alone caught the second call to
  // startSession() itself, but the duplicate click still went through to
  // the DOM — this stops the duplicate dispatch at the source.
  const [sessionStarting, setSessionStarting] = useState(false)
  const sessionIdStateRef = useRef(null)
  // startSession() and the resume-on-mount effect both build `order`/`oi`
  // for a session from an independent getQueue() snapshot, with no
  // coordination between them. A session startSession() just created is
  // itself "active", so the resume effect's own resumeSession() call finds
  // it and races to rebuild order mid-flight — whichever snapshot lands
  // second wins and silently drops whoever had already been marked
  // CALLED/PENDING between the two reads, skipping them without ever
  // dialing. Set the instant startSession begins so the resume effect can
  // tell "a fresh start is already in control of this mount" apart from
  // "the page just (re)loaded onto a session nobody here has touched yet".
  const startingSessionRef = useRef(false)
  // Mirrors queue[].queueId, written synchronously the moment enqueueQueue
  // resolves — a plain `queue` state read can lose a race against a very
  // fast first call (e.g. an immediate "No Answer" click): setQueue's
  // re-render isn't guaranteed to land before the next queue-status write
  // needs queueId. Keyed by person id (matches how the queue array is keyed).
  const queueIdByPersonIdRef = useRef({})
  const [sec, setSec] = useState(0)
  const [count, setCount] = useState(3)
  const [muted, setMuted] = useState(false)
  const drag = useDragOffset()
  const [showPad, setShowPad] = useState(false)
  const [pad, setPad] = useState("")
  const [manual, setManual] = useState(false)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [profilePerson, setProfilePerson] = useState(null)
  const [log, setLog] = useState({ dispo: "", meetingAt: null, notes: "" })
  // Seeded from the Dialer options rail when the rep set a window there, so the
  // choice they made before pressing Dial is the one the session starts with.
  // The dialer stays the authority afterwards — its own controls still edit
  // these, and nothing writes back to the rail mid-session.
  const railCallingHours = useVoiceSessionStore((s) => s.callingHours)
  const railCallerNumber = useVoiceSessionStore((s) => s.callerNumber)
  const [winFrom, setWinFrom] = useState(railCallingHours?.start || "09:00")
  const [winTo, setWinTo] = useState(railCallingHours?.end || "18:00")
  const tRef = useRef(null)
  const {
    status: devStatus,
    notReadyRef,
    reconnecting,
    networkDropped,
    clearNetworkDropped,
    makeCall,
    logCall,
    watchCallStream,
    sendDigits,
    hangup: deviceHangup,
    endSession: completeDialerSession,
    resumeSession,
    ensureSession,
    enqueueQueue,
    getQueue,
    reserveCall,
    markQueueEntryStatus
  } = useVoiceDevice()
  const [outcome, setOutcome] = useState(null)
  const [aiSummary, setAiSummary] = useState(null)
  const [summaryFailed, setSummaryFailed] = useState(false)
  const [enriching, setEnriching] = useState(false)
  const bulkQc = useQueryClient()
  const resolvePhone = useResolveCallPhone()
  const { mutateAsync: enrichBatch } = useEnrichPeople()
  const liveCallRef = useRef(null)
  const callSidRef = useRef(null)
  // The reserved dialer_calls row's own id, from reserveCall — set BEFORE
  // Twilio is ever invoked, so it exists even when voiceAnswer never gets
  // far enough to attach a real twilio_call_sid (e.g. the callee's calling
  // hours have closed, so it returns its own TwiML without ever reaching the
  // <Dial>/attach code at all). logCall falls back to this when the
  // call-sid lookup finds nothing, so ending/logging a call that never
  // really rang still updates the row the rep can see on screen.
  const reservedCallIdRef = useRef(null)
  const stopWatchingRef = useRef(() => {})
  const answeredRef = useRef(false)
  // Set right before hangUp()/noAnswer() call .disconnect() on the live
  // call, so the ringing effect's own end() (wired to that SAME call's
  // "disconnect"/"cancel"/"reject"/"error" events, for when TWILIO ends the
  // call) can tell "the rep just decided this call's outcome" apart from "the
  // call ended on its own" and skip re-deciding it. Without this, hangUp()'s
  // own disconnect() call raced its own listener: hangUp set stage to "log"
  // for the rep to fill in a disposition, then the disconnect event it just
  // caused fired end() a moment later, saw answeredRef still false (calling
  // hours had closed, so the call never really connected), and silently
  // auto-advanced the queue instead — undoing hangUp's own action and
  // skipping the log screen entirely.
  const manualEndRef = useRef(false)
  const [held, setHeld, toggleHold] = useHoldToggle(tokenInfo, callSidRef)
  useEffect(() => {
    liveCallRef.current?.mute(muted)
  }, [muted])
  const [tzSel, setTzSel] = useState(() => new Set(BDL_TZ.map((t) => t[0])))
  const toggleTz = (key) =>
    setTzSel((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  // A contact with no known timezone is always kept: a missing location must not
  // drop them from the queue.
  const tzIncluded = (q) => q.tz == null || tzSel.has(q.tz)

  const total = queue.length
  const cursor = order.length ? order[oi] : 0
  const cur = queue[cursor] || queue[0]
  const talkingPoints = useDialerTalkingPoints(
    tokenInfo,
    cur?.person?.id,
    stage === "ringing" || stage === "live",
    liveAt,
    stage === "live"
  )
  const done = order.slice(0, oi).length
  const nextCur = order[oi + 1] != null ? queue[order[oi + 1]] : null

  const clearTimers = () => {
    clearInterval(tRef.current)
    tRef.current = null
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // startSession() creating a session makes it "active", so this
      // effect's own resumeSession() call below would find and race to
      // rebuild order/oi out from under whatever startSession() is doing
      // in the same mount (see startingSessionRef's declaration comment).
      // A fresh start always wins outright.
      if (startingSessionRef.current) {
        setResuming(false)
        return
      }
      const activeSessionId = await resumeSession()
      if (cancelled || startingSessionRef.current) return
      if (!activeSessionId) {
        setResuming(false)
        return
      }
      // The full queue, not just the next pending entry — resuming onto a
      // single entry collapsed `order` to length 1, so the very next call
      // ending satisfied goTo's oi+1 >= order.length check and completed the
      // session with every other still-PENDING person abandoned for good.
      const fullQueue = await getQueue(activeSessionId)
      if (cancelled || startingSessionRef.current) return
      const pending = fullQueue
        .filter((e) => e.status === "PENDING")
        .sort((a, b) => a.sequence - b.sequence)
      if (!pending.length) {
        setResuming(false)
        return
      }
      const resumeOrder = []
      pending.forEach((entry) => {
        const idx = queue.findIndex(
          (q) => String(q.id) === String(entryPersonId(entry))
        )
        if (idx === -1) return
        queueIdByPersonIdRef.current[String(entryPersonId(entry))] = entry.id
        resumeOrder.push(idx)
      })
      if (!resumeOrder.length) {
        setResuming(false)
        return
      }
      if (startingSessionRef.current) return
      sessionIdStateRef.current = activeSessionId
      setQueue((qs) =>
        qs.map((q) => {
          const entry = pending.find(
            (e) => String(entryPersonId(e)) === String(q.id)
          )
          return entry ? { ...q, queueId: entry.id } : q
        })
      )
      setOrder(resumeOrder)
      setOi(0)
      setStage("countdown")
      setResuming(false)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (stage !== "countdown" || manual) return
    if (count <= 0) {
      setStage(cur && !cur.number ? "finding" : "ringing")
      return
    }
    const t = setTimeout(() => setCount((c) => c - 1), 720)
    return () => clearTimeout(t)
  }, [stage, count, manual, cur])

  useEffect(() => {
    if (stage !== "finding" || manual) return
    let cancelled = false
    ;(async () => {
      const found = await resolvePhone({
        id: cur?.person?.id,
        phone: cur?.person?.phone,
        phone_checked: cur?.person?.phone_checked,
        linkedin_url: cur?.person?.linkedin_url
      })
      if (cancelled) return
      if (found) {
        patchCur({ number: found })
        setStage("ringing")
        return
      }
      patchCur({ status: "skipped", dispo: "No number found" })
      markCurrentQueueEntry("SKIPPED")
      goTo(oi + 1)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, manual, cursor])
  useEffect(() => {
    if (!networkDropped) return
    setOutcome("NETWORK_LOST")
    patchCur({ status: "skipped", dispo: "Connection lost" })
    setStage("paused")
    clearNetworkDropped()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkDropped])

  useEffect(() => {
    if (stage !== "ringing" || manual) return
    setAiSummary(null)
    let cancelled = false
    // The queue moves on when a call ends, so anything this call reports later has
    // to name the person it was about. Without it a second end event stamped the
    // outcome on whoever the cursor had advanced to — someone never dialled.
    const dialedAt = cursor
    // sessionIdStateRef is a live ref, not a value this effect closes over —
    // reading it fresh here would pair whatever session is CURRENT with the
    // queue_id this effect instance computed for the OLD one. A prior
    // session's ringing effect can still be mid-await when a fresh Start
    // creates a brand new session: by the time its reserveCall call fires,
    // sessionIdStateRef.current already points at the new session while
    // activeQueueId is still the old session's — dialer-service correctly
    // rejects that pairing, but by then the call has already gone out under
    // the wrong session's
    // identity. Captured once, at the moment this effect instance's own
    // queue_id is computed below, so a mismatch against the live ref can be
    // treated exactly like `cancelled`.
    const sessionIdAtStart = sessionIdStateRef.current
    let ended = false
    // Filled in once the call object exists, so cleanup can detach its
    // listeners below — see the "off" calls in the returned function.
    let attachedCall = null
    let endHandler = null
    answeredRef.current = false
    reservedCallIdRef.current = null
    manualEndRef.current = false
    ;(async () => {
      const to = cur && cur.number
      const params = {}
      if (dialerCfg.countryMatch && cur.person?.location_country)
        params.calleeCountry = cur.person.location_country
      if (dialerCfg.rotation && dialerCfg.rotateN)
        params.rotateAfter = String(dialerCfg.rotateN)
      if (cur.person?.listId) params.list_id = cur.person.listId
      const activeQueueId =
        queueIdByPersonIdRef.current[String(cur?.id)] ?? cur?.queueId
      if (sessionIdAtStart && activeQueueId && devStatus === "ready" && to) {
        const reservedCallId = await reserveCall(
          sessionIdAtStart,
          activeQueueId,
          to,
          // Boost Connect (the default) leaves this undefined, which is what
          // makes dialer-service choose the number matching the callee. A rep
          // who picked a specific number in the rail forces that one instead.
          railCallerNumber || undefined,
          cur.person?.linkedin_url,
          cur.person?.listId
        )
        if (cancelled || sessionIdStateRef.current !== sessionIdAtStart) return
        if (reservedCallId) {
          params.queue_id = String(activeQueueId)
          reservedCallIdRef.current = reservedCallId
        }
      }
      if (cancelled || sessionIdStateRef.current !== sessionIdAtStart) return
      const call =
        devStatus === "ready" && to
          ? await makeCall(
              to,
              Object.keys(params).length ? params : undefined,
              cur.person?.id,
              { start: winFrom, end: winTo },
              cur.person?.linkedin_url
            )
          : null
      if (cancelled) return
      if (!call) {
        const reasonOutcome = dlNotReadyOutcome(notReadyRef.current)
        setOutcome(reasonOutcome)
        patchCur({
          status: "skipped",
          dispo: dlOutcomeLabel(reasonOutcome)
        })
        setStage("paused")
        return
      }
      liveCallRef.current = call
      call.on("accept", () => {
        callSidRef.current = call.parameters?.CallSid ?? null
        setAiSummary(null)
        setSummaryFailed(false)
        bulkQc.invalidateQueries({ queryKey: ["people-os"] })
        stopWatchingRef.current = watchCallStream(callSidRef.current, {
          onStatus: (status) => {
            setOutcome(status)
            if (status === "ANSWERED") {
              answeredRef.current = true
              setStage("live")
              setLiveAt(Date.now())
              setLeftTab("talk")
              setRailOpen(true)
              setSessionMin(true)
              setMiniTalk(true)
            }
          },
          onSummary: setAiSummary,
          onFailed: () => setSummaryFailed(true)
        })
      })
      // Twilio can report the same ending more than once — disconnect after cancel,
      // for instance — and every one of these used to advance the queue again.
      const end = () => {
        if (ended) return
        if (manualEndRef.current) return
        // The listener now outlives the effect run that attached it, so a late
        // event from a call the rep has already moved past must not re-decide an
        // outcome that belongs to a newer call. Identity, not effect lifetime,
        // is what makes that safe.
        if (
          attachedCall &&
          liveCallRef.current &&
          liveCallRef.current !== attachedCall
        )
          return
        ended = true
        clearTimers()
        liveCallRef.current = null
        if (answeredRef.current) {
          setStage((s) => (s === "live" ? "log" : s))
        } else {
          patchAt(dialedAt, { status: "noanswer", durationSec: 0 })
          goTo(oi + 1)
        }
      }
      call.on("disconnect", end)
      call.on("cancel", end)
      call.on("reject", end)
      call.on("error", end)
      // Stashed so cleanup can detach these below. Without this, a busy/no-answer
      // event Twilio reports late — after the rep has already redialed and gotten
      // a real, disposed outcome on a NEW call object — still fired on this
      // abandoned one and overwrote the fresh disposition with "noanswer", plus
      // advanced the queue a second time via goTo.
      attachedCall = call
      endHandler = end
    })()
    return () => {
      cancelled = true
      // Detach only when this call is being abandoned, never merely because the
      // effect re-ran. `stage` is a dependency and the answer handler sets it to
      // "live", so a call being ANSWERED tears this effect down — and detaching
      // there took the disconnect listener off the call that was still up. The
      // far end then hung up and nothing was listening: the timer ran on and the
      // panel showed a live call forever. Every path that genuinely abandons a
      // call nulls or replaces liveCallRef before the teardown, so an identity
      // check tells the two apart.
      if (attachedCall && endHandler && liveCallRef.current !== attachedCall) {
        attachedCall.off("disconnect", endHandler)
        attachedCall.off("cancel", endHandler)
        attachedCall.off("reject", endHandler)
        attachedCall.off("error", endHandler)
      }
    }
  }, [stage, oi, manual, devStatus, watchCallStream])
  useEffect(() => {
    return () => stopWatchingRef.current()
  }, [])
  useEffect(() => {
    if (stage !== "live") return
    tRef.current = setInterval(() => setSec((s) => s + 1), 1000)
    return () => clearInterval(tRef.current)
  }, [stage, oi])
  // The dock is only for a call in progress. Its own hang-up button used to be the one thing
  // that left it, so a call the other side ended stranded the rep in it with no way back to
  // the log panel. Leaving is now tied to the call ending rather than to a button.
  useEffect(() => {
    if (stage === "live") return
    setSessionMin(false)
    setMiniTalk(false)
  }, [stage])

  const patchAt = (index, patch) =>
    setQueue((qs) => qs.map((q, i) => (i === index ? { ...q, ...patch } : q)))
  const patchCur = (patch) => patchAt(cursor, patch)

  const markCurrentQueueEntry = (status) => {
    // Prefer the ref: it's written synchronously the instant enqueueQueue
    // resolves, whereas cur?.queueId depends on a setQueue re-render having
    // landed first — a fast "No Answer" on the very first call in a session
    // can otherwise fire before that re-render commits (see queueIdByPersonIdRef).
    const queueId =
      queueIdByPersonIdRef.current[String(cur?.id)] ?? cur?.queueId
    if (sessionIdStateRef.current && queueId) {
      markQueueEntryStatus(sessionIdStateRef.current, queueId, status)
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        "[dialer] markCurrentQueueEntry skipped: missing sessionId or queueId",
        { sessionId: sessionIdStateRef.current, queueId, status }
      )
    }
  }

  const goTo = (nextOi, ord = orderRef.current) => {
    clearTimers()
    if (nextOi >= ord.length) {
      startingSessionRef.current = false
      setSessionStarting(false)
      completeDialerSession()
      setStage("done")
      return
    }
    setOi(nextOi)
    setSec(0)
    setOutcome(null)
    setMuted(false)
    setHeld(false)
    setShowPad(false)
    setPad("")
    setCount(3)
    setLog({ dispo: "", meetingAt: null, notes: "" })
    setShowProfile(false)
    setProfilePerson(null)
    setStage("countdown")
  }

  const included = queue.filter(tzIncluded).length
  const needEnrich = queue.filter((q) => !q.number).length
  const enrichMissing = async () => {
    const missing = queue.filter((q) => !q.number && q.person?.id)
    if (!missing.length) return
    setEnriching(true)
    try {
      const res = await enrichBatch({
        person_ids: missing.map((q) => String(q.person.id)),
        mode: "phone"
      })
      const byId = new Map(
        (res?.enriched ?? [])
          .filter((e) => e.phone)
          .map((e) => [String(e.person_id), e.phone])
      )
      if (byId.size) {
        setQueue((qs) =>
          qs.map((x) =>
            !x.number && byId.has(String(x.person?.id))
              ? { ...x, number: byId.get(String(x.person.id)) }
              : x
          )
        )
      }
    } catch {
    } finally {
      setEnriching(false)
    }
    if (onEnrich) onEnrich()
  }
  const startSession = async () => {
    // No disabled-while-in-flight guard on the Start button — an impatient
    // double-click fired this twice for the same click, each call creating
    // its OWN session and enqueueQueue result but both writing into the
    // same shared queueIdByPersonIdRef. Whichever finished last won, and
    // the loser's already-fired ringing effect went on dialing with a
    // queue_id belonging to a session that was no longer the active one —
    // confirmed live via a reserve-call for a queue_id that turned out to
    // belong to an entirely different, earlier session.
    if (startingSessionRef.current) {
      return
    }
    startingSessionRef.current = true
    setSessionStarting(true)
    const ord = []
    queue.forEach((q, i) => {
      if (tzIncluded(q)) ord.push(i)
    })
    setQueue((qs) =>
      qs.map((q) => (tzIncluded(q) ? q : { ...q, status: "skipped" }))
    )
    setOrder(ord)
    if (!ord.length) {
      startingSessionRef.current = false
      setSessionStarting(false)
      completeDialerSession()
      setStage("done")
      return
    }

    // force: true — a fresh Start click must always get a brand-new
    // session, never the stale one the resume-on-mount flow may have
    // already cached in sessionIdRef (see ensureSession's own comment).
    const sessionId = await ensureSession({ start: winFrom, end: winTo }, true)
    sessionIdStateRef.current = sessionId
    // Every dial in this session reports its outcome by queueId
    // (markCurrentQueueEntry silently no-ops without one, and
    // reconcileSessionStats has nothing to count). If session creation or
    // the enqueue call fails, the dialer used to fall straight through to
    // goTo(0, ord) anyway and run the whole UI flow untethered from any
    // real backend record -- every call showed as dialed/connected on
    // screen while dialer_calls/dialer_queue stayed completely empty.
    if (!sessionId) {
      startingSessionRef.current = false
      setSessionStarting(false)
      setOrder([])
      setStage("paused")
      setOutcome("SESSION_ERROR")
      return
    }
    const peopleIds = ord.map((idx) => String(queue[idx].id))
    const enqueued = await enqueueQueue(sessionId, peopleIds)
    if (!enqueued) {
      startingSessionRef.current = false
      setSessionStarting(false)
      setOrder([])
      setStage("paused")
      setOutcome("SESSION_ERROR")
      return
    }
    enqueued.forEach((e) => {
      queueIdByPersonIdRef.current[String(entryPersonId(e))] = e.id
    })
    setQueue((qs) =>
      qs.map((q) => {
        const entry = enqueued.find(
          (e) => String(entryPersonId(e)) === String(q.id)
        )
        return entry ? { ...q, queueId: entry.id } : q
      })
    )

    goTo(0, ord)
  }

  const hangUp = () => {
    clearTimers()
    if (liveCallRef.current) {
      manualEndRef.current = true
      liveCallRef.current.disconnect()
      liveCallRef.current = null
    }
    setStage("log")
  }
  const noAnswer = (kind) => {
    clearTimers()
    if (liveCallRef.current) {
      manualEndRef.current = true
      liveCallRef.current.disconnect()
      liveCallRef.current = null
    }
    patchCur({ status: kind, durationSec: 0 })
    // The manual "Skip" button routes through here with kind: "skipped", but
    // this always reported "CALLED" to the backend regardless — so a skip
    // never showed up in dialer_sessions.skipped_count, only ever counted as
    // a call that happened.
    markCurrentQueueEntry(kind === "skipped" ? "SKIPPED" : "CALLED")
    goTo(oi + 1)
  }
  const redial = () => {
    clearTimers()
    setSec(0)
    setOutcome(null)
    // Mute, hold and the keypad belong to the call that just ended. Carrying them
    // into the next one left a rep dialling out already muted.
    setMuted(false)
    setHeld(false)
    setShowPad(false)
    setPad("")
    setCount(2)
    setStage("countdown")
  }

  const commitLog = () => {
    const dur = Math.max(sec, 1)
    const summary = { tldr: aiSummary, failed: summaryFailed }
    logCall(callSidRef.current, {
      disposition: log.dispo || undefined,
      notes: log.notes || undefined,
      meetingAt: log.meetingAt,
      fallbackCallId: reservedCallIdRef.current
    })
    callSidRef.current = null
    patchCur({
      status: "connected",
      durationSec: dur,
      dispo: log.dispo,
      meetingAt: log.meetingAt,
      notes: log.notes,
      summary
    })
    if (onLogCall)
      onLogCall(cur.person, {
        id: "call-" + Date.now(),
        type: "call",
        dir: "out",
        outcome: "connected",
        durationSec: dur,
        duration: dlFmt(dur),
        when: "Just now",
        date: "Today",
        rep,
        disposition: log.dispo,
        meetingAt: log.meetingAt,
        notes: log.notes,
        summary
      })
  }
  const logAndNext = () => {
    commitLog()
    markCurrentQueueEntry("CALLED")
    goTo(oi + 1)
  }
  const logAndPause = () => {
    commitLog()
    clearTimers()
    setStage("paused")
  }
  // Re-entering through goTo rather than flipping the stage directly: it resets the
  // per-call state the pause left behind, and if the cursor has already run past the
  // end it closes the session instead of counting down to nobody.
  const resume = () => goTo(oi)
  const cutLiveCall = () => {
    if (liveCallRef.current) {
      liveCallRef.current.disconnect()
      liveCallRef.current = null
    }
    // Belt and braces: the Device holds its own handle on the call and outlives
    // this component, so dropping only the local ref left the leg up.
    deviceHangup()
  }

  const terminateSession = () => {
    clearTimers()
    cutLiveCall()
    startingSessionRef.current = false
    setSessionStarting(false)
    completeDialerSession()
    setStage("done")
    if (onClose) onClose()
  }

  // Nothing is in flight during setup, so closing then needs no ceremony.
  const sessionInFlight = stage !== "setup" && stage !== "done"
  const requestClose = () => {
    if (sessionInFlight) setConfirmEnd(true)
    else if (onClose) onClose()
  }

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"]
  const sub = {
    2: "ABC",
    3: "DEF",
    4: "GHI",
    5: "JKL",
    6: "MNO",
    7: "PQRS",
    8: "TUV",
    9: "WXYZ",
    0: "+"
  }
  const av = (p) => p.initials || dlInit(p.name)
  const endConfirm = confirmEnd && (
    <div className="bdl-confirm-scrim" onMouseDown={() => setConfirmEnd(false)}>
      <div
        className="bdl-confirm"
        role="dialog"
        aria-modal="true"
        aria-label="End this session"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="bdl-confirm-t">
          {I.phoneOff({ size: 15 })} End this session?
        </span>
        <p className="bdl-confirm-b">
          {stage === "live" || stage === "ringing"
            ? "The call in progress will be cut and the rest of the queue dropped."
            : "The rest of the queue will be dropped."}
        </p>
        <div className="bdl-confirm-ctl">
          <button
            className="bdl-confirm-no"
            onClick={() => setConfirmEnd(false)}
          >
            Keep dialling
          </button>
          <button
            className="bdl-confirm-yes"
            onClick={() => {
              setConfirmEnd(false)
              terminateSession()
            }}
          >
            End session
          </button>
        </div>
      </div>
    </div>
  )

  const manualDialer = manual && (
    <Dialer4L
      person={{
        name: "Ad-hoc call",
        title: "Manual dial · mid-session",
        phone: ""
      }}
      rep={rep}
      onClose={() => setManual(false)}
    />
  )

  if (resuming) {
    return (
      <div className="bdl-scrim">
        <div className="bdl bdl-setupwrap" role="dialog" aria-modal="true">
          <div
            className="bdl-boot"
            role="status"
            aria-label="Opening the dialer"
          >
            <span className="bdl-boot-spin" />
          </div>
        </div>
      </div>
    )
  }

  if (stage === "setup") {
    return (
      <React.Fragment>
        {addNumOpen && (
          <AddNumberModal
            tokenInfo={tokenInfo}
            onClose={() => setAddNumOpen(false)}
          />
        )}
        <div className="bdl-scrim" onMouseDown={() => onClose && onClose()}>
          <div
            className="bdl bdl-setupwrap"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Set up call session"
          >
            <div className="bdl-hd">
              <span className="bdl-hd-t">
                {I.phone({ size: 14 })} Set up call session
              </span>
              <span className="bdl-hd-sp" />
              <button
                className="dlr-x"
                onClick={() => onClose && onClose()}
                aria-label="Close"
              >
                {I.close({ size: 16 })}
              </button>
            </div>
            <div className="bdl-setup">
              <div className="bdl-setup-lead">
                <div className="bdl-setup-big">
                  {included}
                  <span> / {total}</span>
                </div>
                <div className="bdl-setup-cap">
                  contacts ready to dial in this session
                </div>
              </div>

              <div className="bdl-sec">
                <button
                  type="button"
                  className="bdl-recapline"
                  onClick={() => setNumbersOpen((o) => !o)}
                  title={numbersOpen ? "Collapse" : "Edit call from numbers"}
                  aria-expanded={numbersOpen}
                >
                  <span className="bdl-recapline-ic">
                    {I.phone({ size: 13 })}
                  </span>
                  <span className="bdl-recapline-l">Call from numbers</span>
                  {!numbersOpen && (
                    <span className="bdl-recapline-v">
                      {dialerCfg.countryMatch
                        ? "Country code matching"
                        : (dialerCfg.numbers[0] || {}).number}{" "}
                      <em>· {dialerCfg.numbers.length} numbers</em>
                    </span>
                  )}
                  <span className="bdl-recapline-go">
                    {numbersOpen
                      ? I.chevD({ size: 14 })
                      : I.chevR({ size: 14 })}
                  </span>
                </button>

                {numbersOpen && (
                  <div className="bdl-numpanel">
                    <div className="bdl-numtoggle">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={dialerCfg.countryMatch}
                        aria-label="Country code matching"
                        onClick={() =>
                          patchCfg({ countryMatch: !dialerCfg.countryMatch })
                        }
                        className={
                          "bdl-numsw" + (dialerCfg.countryMatch ? " is-on" : "")
                        }
                      >
                        <span />
                      </button>
                      <span className="bdl-numtoggle-tx">
                        <b>Country code matching</b>
                        <span>
                          Use a caller ID matching each contact's country
                        </span>
                      </span>
                    </div>

                    <div className="bdl-numlist">
                      {dialerCfg.numbers.map((n) => (
                        <div
                          className={"bdl-numitem" + (n.spam ? " is-spam" : "")}
                          key={n.id}
                        >
                          <span className="bdl-numflag">{n.cc}</span>
                          <span className="bdl-numitem-main">
                            <span className="bdl-numitem-num">
                              {n.number}
                              {n.spam ? (
                                <span className="bdl-spamtag">
                                  {I.alert({ size: 10 })} Spam-flagged
                                </span>
                              ) : null}
                            </span>
                            <span className="bdl-numitem-meta">
                              {Number.isFinite(Number(n.connects))
                                ? Math.round(Number(n.connects)) +
                                  "% connected · "
                                : ""}
                              {n.calls || 0} calls · {n.region}
                            </span>
                          </span>
                          <button
                            className={
                              "bdl-numflagbtn" + (n.spam ? " is-on" : "")
                            }
                            onClick={() => toggleSpam(n.id)}
                            title={n.spam ? "Clear spam flag" : "Mark as spam"}
                          >
                            {I.alert({ size: 13 })}
                          </button>
                          <button
                            className="bdl-numdel"
                            onClick={() => removeNum(n.id)}
                            title="Remove"
                          >
                            {I.close({ size: 13 })}
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="bdl-numadd"
                      onClick={() => setAddNumOpen(true)}
                    >
                      {I.plus({ size: 13 })} Add a new number
                    </button>

                    <div className="bdl-numrot">
                      <div className="bdl-numtoggle">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={dialerCfg.rotation}
                          aria-label="Number rotation"
                          onClick={() =>
                            patchCfg({ rotation: !dialerCfg.rotation })
                          }
                          className={
                            "bdl-numsw" + (dialerCfg.rotation ? " is-on" : "")
                          }
                        >
                          <span />
                        </button>
                        <span className="bdl-numtoggle-tx">
                          <b>Number rotation</b>
                          <span>
                            Spread volume across lines to avoid overuse
                          </span>
                        </span>
                      </div>
                      {dialerCfg.rotation && (
                        <div className="bdl-rotslider">
                          <span className="bdl-rotlbl">
                            Rotate after <b>{dialerCfg.rotateN}</b> calls /
                            number
                          </span>
                          <input
                            type="range"
                            min="10"
                            max="50"
                            step="5"
                            value={dialerCfg.rotateN || 20}
                            onChange={(e) =>
                              patchCfg({ rotateN: +e.target.value })
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="bdl-sec">
                <button
                  type="button"
                  className="bdl-recapline"
                  onClick={() => setTzOpen((o) => !o)}
                  title={tzOpen ? "Collapse" : "Choose timezones to dial"}
                  aria-expanded={tzOpen}
                >
                  <span className="bdl-recapline-ic">
                    {I.clock({ size: 13 })}
                  </span>
                  <span className="bdl-recapline-l">Timezones</span>
                  {!tzOpen && (
                    <span className="bdl-recapline-v">
                      {tzSel.size === BDL_TZ.length
                        ? "All timezones"
                        : `${tzSel.size} of ${BDL_TZ.length}`}{" "}
                      <em>· {included} people</em>
                    </span>
                  )}
                  <span className="bdl-recapline-go">
                    {tzOpen ? I.chevD({ size: 14 }) : I.chevR({ size: 14 })}
                  </span>
                </button>

                {tzOpen && (
                  <div className="bdl-tzpanel">
                    <div className="bdl-tzhint">
                      Only contacts in the selected timezones are dialed. Anyone
                      whose location we don&apos;t know is always dialed.
                    </div>
                    <div className="bdl-tzchips">
                      {BDL_TZ.map(([key, label]) => {
                        const n = queue.filter((q) => q.tz === key).length
                        const on = tzSel.has(key)
                        return (
                          <button
                            key={key}
                            type="button"
                            role="switch"
                            aria-checked={on}
                            className={"bdl-tzchip" + (on ? " is-on" : "")}
                            onClick={() => toggleTz(key)}
                          >
                            {on ? I.check({ size: 12 }) : null}
                            {label}
                            <span className="bdl-tzchip-n">{n}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="bdl-sec">
                <button
                  type="button"
                  className="bdl-recapline"
                  onClick={() => setHoursOpen((o) => !o)}
                  title={hoursOpen ? "Collapse" : "Edit calling hours"}
                  aria-expanded={hoursOpen}
                >
                  <span className="bdl-recapline-ic">
                    {I.clock({ size: 13 })}
                  </span>
                  <span className="bdl-recapline-l">Calling hours</span>
                  {!hoursOpen && (
                    <span className="bdl-recapline-v">
                      {winFrom} – {winTo} <em>· prospect's local time</em>
                    </span>
                  )}
                  <span className="bdl-recapline-go">
                    {hoursOpen ? I.chevD({ size: 14 }) : I.chevR({ size: 14 })}
                  </span>
                </button>

                {hoursOpen && (
                  <React.Fragment>
                    <div className="bdl-winrow">
                      <input
                        type="time"
                        onClick={(e) => e.currentTarget.showPicker?.()}
                        className="bdl-wintime"
                        value={winFrom}
                        onChange={(e) => setWinFrom(e.target.value)}
                      />
                      <span className="bdl-winsep">to</span>
                      <input
                        type="time"
                        onClick={(e) => e.currentTarget.showPicker?.()}
                        className="bdl-wintime"
                        value={winTo}
                        onChange={(e) => setWinTo(e.target.value)}
                      />
                    </div>
                    <div className="bdl-tznote">
                      Calls fire only when it's between {winFrom} and {winTo}{" "}
                      for the contact. Outside the window they wait in queue.
                    </div>
                  </React.Fragment>
                )}
              </div>

              {needEnrich > 0 && (
                <div className="bdl-enrich">
                  <span className="bdl-enrich-ic">{I.info({ size: 14 })}</span>
                  <span className="bdl-enrich-tx">
                    <b>{needEnrich}</b>{" "}
                    {needEnrich === 1 ? "contact has" : "contacts have"} no
                    phone number yet. They stay in the queue — a number is
                    looked up when their turn comes, and they are skipped if
                    none is found.
                  </span>
                  <button
                    className="bdl-enrich-btn"
                    onClick={enrichMissing}
                    disabled={enriching}
                  >
                    {I.sparkle({ size: 12 })}{" "}
                    {enriching ? "Enriching…" : `Enrich ${needEnrich}`}
                  </button>
                </div>
              )}
            </div>
            <div className="bdl-foot">
              <button className="btn" onClick={() => onClose && onClose()}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={included === 0 || sessionStarting}
                onClick={startSession}
              >
                {I.play({ size: 13 })} Start session · {included}
              </button>
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  if (stage === "done") {
    // commitLog() sets status: "connected" for every answered, disposed
    // call regardless of outcome, so a call logged as "Left live message"
    // or "Wrong number" still carries status "connected" — dispo is the
    // field that actually holds what the rep chose. noanswer/skipped never
    // get a dispo (the call was never answered to dispose), so those fall
    // back to status.
    const outcomeOf = (q) => q.dispo || q.status
    const tally = {}
    queue.forEach((q) => {
      const outcome = outcomeOf(q)
      tally[outcome] = (tally[outcome] || 0) + 1
    })
    const reached = tally.connected || 0
    // BDL_DONE_STATUS is every outcome that means "we dialed and got a
    // resolved result" (busy/wrongnum/meeting/livemsg included, not just
    // connected/voicemail/noanswer) — the live in-session tally already uses
    // it for liveCalled; this was still its own incomplete inline list, so a
    // session that was all busy signals showed "0/1" instead of "0/3".
    const called = queue.filter((q) =>
      BDL_DONE_STATUS.includes(outcomeOf(q))
    ).length
    const rate = called ? Math.round((reached / called) * 100) : 0
    const connected = queue.filter((q) => q.status === "connected")
    return (
      <div className="bdl-scrim" onMouseDown={() => onClose && onClose()}>
        <div
          className="bdl bdl-recap"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="bdl-hd">
            <span className="bdl-hd-t">
              {I.checkCircle({ size: 15 })} Session complete
            </span>
            <button
              className="dlr-x"
              onClick={() => onClose && onClose()}
              aria-label="Close"
            >
              {I.close({ size: 16 })}
            </button>
          </div>
          <div className="bdl-recap-body">
            <div className="bdl-recap-head">
              <div className="bdl-recap-big">
                {reached}
                <span>/{called}</span>
              </div>
              <div className="bdl-recap-cap">
                connected live · {rate}% connect rate
                {reached > 0 ? ", logged to each contact's timeline" : ""}
              </div>
            </div>
            <div className="bdl-recap-stats">
              {BDL_OUTCOMES.map((o) => (
                <div className="bdl-stat" key={o.id}>
                  <span className={"bdl-stat-ic is-" + o.id}>
                    {I[o.icon]({ size: 15 })}
                  </span>
                  <span className="bdl-stat-n">{tally[o.id] || 0}</span>
                  <span className="bdl-stat-l">{o.label}</span>
                </div>
              ))}
            </div>
            {connected.length > 0 && (
              <div className="bdl-summaries">
                <div className="bdl-sec-t">
                  {I.sparkle({ size: 12 })} Call summaries · auto-captured by
                  OrbitShift
                </div>
                {connected.map((q) => (
                  <div className="bdl-sumcard" key={q.id}>
                    <div className="bdl-sumcard-hd">
                      <span className="dlr-av sm">{av(q.person)}</span>
                      <div className="bdl-sumcard-id">
                        <span className="bdl-rn">{q.person.name}</span>
                        <span className="bdl-sumcard-meta">
                          {dlFmt(q.durationSec)}
                          {q.dispo ? " · " + CALL_DISPO_LABEL[q.dispo] : ""}
                          {q.dispo === "meeting" &&
                          q.meetingAt &&
                          q.meetingAt.date
                            ? " · " + clkFmtMeeting(q.meetingAt)
                            : ""}
                        </span>
                      </div>
                      {q.dispo && (
                        <span
                          className={"clk-chip is-" + CALL_DISPO_CLS[q.dispo]}
                        >
                          {CALL_DISPO_LABEL[q.dispo]}
                        </span>
                      )}
                    </div>
                    {q.summary?.failed ? (
                      <div className="bdl-sumcard-fail">
                        {I.alert({ size: 11 })} Transcript couldn't be generated
                      </div>
                    ) : (
                      <MarkdownRender
                        content={withHardLineBreaks(
                          String(q.summary ? q.summary.tldr || "" : "")
                        )}
                        className="bdl-sumcard-tldr bdl-md"
                      />
                    )}
                    {q.notes && (
                      <div className="bdl-sumcard-note">
                        {I.edit({ size: 11 })} {q.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bdl-foot">
            <button
              className="btn btn-primary"
              onClick={() => onClose && onClose()}
            >
              {I.check({ size: 14 })} Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Same fix as the done-screen tally above: dispo (when set) is the rep's
  // actual logged outcome, status is only "connected"/"skipped"/"noanswer".
  const liveOutcomeOf = (q) => q.dispo || q.status
  const liveTally = {}
  queue.forEach((q) => {
    const outcome = liveOutcomeOf(q)
    liveTally[outcome] = (liveTally[outcome] || 0) + 1
  })
  const liveReached = queue.filter((q) =>
    BDL_REACHED_STATUS.includes(liveOutcomeOf(q))
  ).length
  const liveVm = liveTally.voicemail || 0
  const liveMeetings = queue.filter(
    (q) => q.status === "connected" && q.dispo === "meeting"
  ).length
  const liveCalled = queue.filter((q) =>
    BDL_DONE_STATUS.includes(liveOutcomeOf(q))
  ).length
  const liveRate = liveCalled ? Math.round((liveReached / liveCalled) * 100) : 0
  const liveNums = dialerCfg.numbers.filter((n) => !n.spam)
  const fromNum = dialerCfg.countryMatch
    ? liveNums.find((n) => n.cc === bdlCC(cur.tz)) ||
      liveNums[0] ||
      dialerCfg.numbers[0]
    : liveNums[0] || dialerCfg.numbers[0]

  if (sessionMin) {
    const stLabel =
      stage === "live"
        ? "On call · " + dlFmt(sec)
        : stage === "ringing"
          ? "Ringing…"
          : stage === "finding"
            ? "Looking for a number…"
            : stage === "countdown"
              ? "Dialing in " + count
              : stage === "log"
                ? "Logging call"
                : "Session paused"
    return (
      <div
        className={"bdl-minidock" + drag.className}
        ref={drag.ref}
        style={drag.style}
        onMouseDown={drag.onDragStart}
        onMouseMove={drag.onMouseMove}
        onMouseLeave={drag.onMouseLeave}
      >
        <div
          className={"dlr-mini bdl-mini" + (stage === "live" ? " is-live" : "")}
          role="dialog"
          aria-label="Power dialer session"
        >
          <span
            className={
              "dlr-mini-av" +
              (stage === "live"
                ? " is-live"
                : stage === "ringing"
                  ? " is-ring"
                  : "")
            }
          >
            {av(cur.person)}
          </span>
          <span className="dlr-mini-tx">
            <span className="dlr-mini-nm">{cur.person.name}</span>
            <span className="dlr-mini-st">
              {stLabel} · {done}/{order.length} called
            </span>
          </span>
          {stage === "live" && (
            <button
              type="button"
              className={"dlr-mini-btn" + (muted ? " is-on" : "")}
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? I.micOff({ size: 15 }) : I.mic({ size: 15 })}
            </button>
          )}
          {stage === "live" && (
            <button
              type="button"
              className={"dlr-mini-btn" + (held ? " is-on" : "")}
              onClick={toggleHold}
              title={held ? "Resume call" : "Hold call"}
            >
              {held ? I.play({ size: 15 }) : I.pause({ size: 15 })}
            </button>
          )}
          <button
            type="button"
            className={"dlr-mini-btn" + (miniTalk ? " is-on" : "")}
            onClick={() => setMiniTalk((t) => !t)}
            title={miniTalk ? "Hide talking points" : "Show talking points"}
            aria-expanded={miniTalk}
          >
            {I.sparkle({ size: 15 })}
          </button>
          {/* The only way out of the dock was ending the call. */}
          <button
            type="button"
            className="dlr-mini-btn"
            onClick={() => {
              setSessionMin(false)
              setMiniTalk(false)
            }}
            title="Back to the full dialer"
            aria-label="Back to the full dialer"
          >
            {I.maximize({ size: 15 })}
          </button>
          {(stage === "ringing" || stage === "live") && (
            <button
              type="button"
              className="dlr-mini-hang"
              onClick={() => {
                hangUp()
                setSessionMin(false)
                setMiniTalk(false)
              }}
              title="End call & log"
            >
              {I.phoneOff({ size: 15 })}
            </button>
          )}
        </div>
        {miniTalk && (
          <div className="bdl-minitalk">
            <div className="bdl-railtalk-who">
              {I.phone({ size: 11 })} What to say to{" "}
              {cur.person.name.split(" ")[0]}
            </div>
            <TalkingPointsPanel
              points={talkingPoints.points}
              generating={talkingPoints.generating}
              unavailable={talkingPoints.unavailable}
              open
              ringing={stage === "ringing"}
              onToggle={() => setMiniTalk(false)}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <React.Fragment>
      {addNumOpen && (
        <AddNumberModal
          tokenInfo={tokenInfo}
          onClose={() => setAddNumOpen(false)}
        />
      )}
      <div className="bdl-scrim" onMouseDown={requestClose}>
        <div
          className={"bdl" + drag.className}
          ref={drag.ref}
          style={drag.style}
          onMouseDown={(e) => {
            e.stopPropagation()
            drag.onDragStart(e)
          }}
          onMouseMove={drag.onMouseMove}
          onMouseLeave={drag.onMouseLeave}
          role="dialog"
          aria-modal="true"
          aria-label="Power dialer session"
        >
          <div className="bdl-hd">
            <span className="bdl-hd-t">
              {I.phone({ size: 14 })}{" "}
              {dialerCfg.countryMatch ? "Local presence" : "Session"} ·{" "}
              {(fromNum || {}).number}
            </span>
            <div className="bdl-prog">
              <span>
                {done} / {order.length} called
              </span>
              <span className="bdl-progbar">
                <i style={{ width: (done / order.length) * 100 + "%" }} />
              </span>
            </div>
            <div className="bdl-hdctl">
              <button
                className="bdl-hdbtn"
                onClick={() => {
                  // Whatever the queue is doing, it stops. Two live legs means the
                  // rep is talking over themselves and neither call is recoverable.
                  clearTimers()
                  cutLiveCall()
                  setStage("paused")
                  setManual(true)
                }}
                title="Manual ad-hoc dial — cuts the current call and pauses the sequence"
              >
                {I.keypad({ size: 14 })} Keypad
              </button>
            </div>
            <button className="dlr-x" onClick={requestClose} aria-label="Close">
              {I.close({ size: 16 })}
            </button>
          </div>

          <div className="bdl-dash">
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{liveCalled}</span>
              <span className="bdl-dashl">Dialed</span>
            </div>
            <div className="bdl-dashstat is-good">
              <span className="bdl-dashn">{liveReached}</span>
              <span className="bdl-dashl">Reached</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{liveVm}</span>
              <span className="bdl-dashl">Voicemail</span>
            </div>
            <div className="bdl-dashstat is-accent">
              <span className="bdl-dashn">{liveMeetings}</span>
              <span className="bdl-dashl">Meetings</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{liveRate}%</span>
              <span className="bdl-dashl">Connect rate</span>
            </div>
            <div className="bdl-dashstat">
              <span className="bdl-dashn">{order.length - done}</span>
              <span className="bdl-dashl">Remaining</span>
            </div>
          </div>

          <div className="bdl-body">
            {stage === "log" ? (
              <div className="bdl-rail is-summary">
                <BdlSummaryRail
                  tokenInfo={tokenInfo}
                  peopleId={cur?.person?.id}
                  callSid={callSidRef.current}
                  durationSec={sec}
                  connected={answeredRef.current}
                />
              </div>
            ) : !railOpen ? (
              <button
                type="button"
                className="bdl-railtab"
                onClick={() => setRailOpen(true)}
                title="Show panel"
              >
                {I.chevR({ size: 14 })}
                <span className="bdl-railtab-tx">
                  {leftTab === "talk"
                    ? "Talking points"
                    : "Queue · " + order.length}
                </span>
              </button>
            ) : (
              <div className="bdl-rail">
                <div className="bdl-railtabs">
                  <button
                    type="button"
                    className={
                      "bdl-rtab" + (leftTab === "queue" ? " is-on" : "")
                    }
                    onClick={() => setLeftTab("queue")}
                  >
                    Queue · {order.length}
                  </button>
                  <button
                    type="button"
                    className={
                      "bdl-rtab" + (leftTab === "talk" ? " is-on" : "")
                    }
                    onClick={() => setLeftTab("talk")}
                    title="Talking points"
                  >
                    Talking points
                  </button>
                  <button
                    type="button"
                    className="bdl-railhide"
                    onClick={() => setRailOpen(false)}
                    title="Hide panel"
                  >
                    {I.chevR({ size: 14 })}
                  </button>
                </div>
                {leftTab === "talk" ? (
                  <div className="bdl-railtalk">
                    <div className="bdl-railtalk-who">
                      {I.phone({ size: 11 })} What to say to{" "}
                      {cur?.person?.name?.split(" ")[0]}
                    </div>
                    <TalkingPointsPanel
                      points={talkingPoints.points}
                      generating={talkingPoints.generating}
                      unavailable={talkingPoints.unavailable}
                      open
                      ringing={stage === "ringing"}
                      onToggle={() => setLeftTab("queue")}
                    />
                  </div>
                ) : (
                  <>
                    {order.map((qi, i) => {
                      const q = queue[qi]
                      return (
                        <button
                          className={
                            "bdl-rail-row" +
                            (i === oi ? " is-cur" : "") +
                            (i < oi ? " is-past" : "")
                          }
                          key={q.id}
                          onClick={() => {
                            setProfilePerson(q.person)
                            setShowProfile(true)
                          }}
                          title={"Open " + q.person.name + "'s details"}
                        >
                          <span
                            className={
                              "bdl-dot is-" + (i === oi ? "active" : q.status)
                            }
                          />
                          <span className="bdl-rail-main">
                            <span className="bdl-rn">{q.person.name}</span>
                            <span className="bdl-rt">
                              {q.person.account
                                ? q.person.account.name
                                : q.person.title}
                            </span>
                          </span>
                          <span className="bdl-rail-open">
                            {I.external({ size: 13 })}
                          </span>
                          <span className="bdl-rst">
                            {i === oi ? "Now" : BDL_STLABEL[q.status]}
                          </span>
                        </button>
                      )
                    })}
                  </>
                )}
              </div>
            )}

            <div className={"bdl-stage" + (stage === "log" ? " is-log" : "")}>
              {stage === "log" ? (
                <div className="bdl-logbody">
                  <CallLock4L
                    person={cur.person}
                    durationSec={sec}
                    outcome={outcome}
                    log={log}
                    setLog={setLog}
                    onRedial={redial}
                    variant="wide"
                  />
                  <div className="bdl-logbtns">
                    <button
                      className="bdl-logsecond"
                      disabled={!log.dispo}
                      onClick={logAndPause}
                    >
                      {I.check({ size: 13 })} Log &amp; pause
                    </button>
                    <button
                      className="dlr-callbtn bdl-lognext"
                      disabled={!log.dispo}
                      onClick={logAndNext}
                    >
                      {I.check({ size: 15 })} Log &amp; dial next{" "}
                      {nextCur ? "· " + nextCur.person.name.split(" ")[0] : ""}
                    </button>
                  </div>
                </div>
              ) : stage === "paused" ? (
                <React.Fragment>
                  <span className="bdl-pausebadge">
                    {I.pause({ size: 22 })}
                  </span>
                  <div className="dlr-name">Session paused</div>
                  <div className="dlr-role">
                    {done} of {order.length} contacts called ·{" "}
                    {order.length - done} remaining
                  </div>
                  <div className="bdl-composebtns">
                    <button className="dlr-callbtn" onClick={resume}>
                      {I.play({ size: 16 })} Resume
                      {cur ? " · " + cur.person.name.split(" ")[0] : ""}
                    </button>
                    <button
                      className="bdl-skip"
                      onClick={() => setConfirmEnd(true)}
                    >
                      End session {I.chevR({ size: 15 })}
                    </button>
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <span
                    className={
                      "dlr-av" + (stage === "ringing" ? " is-ring" : "")
                    }
                  >
                    {av(cur.person)}
                  </span>
                  <DlrName
                    name={cur.person.name}
                    linkedinUrl={cur.person.linkedin_url}
                  />
                  <div className="dlr-role">
                    {cur.person.title}
                    {cur.person.account ? " · " + cur.person.account.name : ""}
                  </div>
                  <button
                    className="bdl-viewprofile"
                    onClick={() => {
                      setProfilePerson(cur.person)
                      setShowProfile(true)
                    }}
                  >
                    {I.user({ size: 13 })} Pre-meeting brief
                  </button>

                  {stage === "finding" && (
                    <div className="bdl-count">
                      {I.info({ size: 15 })} Looking for a number for{" "}
                      <b>{cur.person?.name}</b>
                    </div>
                  )}

                  {stage === "countdown" && (
                    <React.Fragment>
                      <div className="bdl-count">
                        {I.phone({ size: 15 })} Dialing in <b>{count}</b>
                      </div>
                      <div className="dlr-num">{cur.number}</div>
                      <div className="bdl-composebtns">
                        <button
                          className="dlr-callbtn"
                          onClick={() => {
                            setCount(0)
                            setStage("ringing")
                          }}
                        >
                          {I.phone({ size: 16 })} Dial now
                        </button>
                        <div className="bdl-countctl">
                          <button
                            className="bdl-skip"
                            onClick={() => setStage("paused")}
                          >
                            {I.pause({ size: 13 })} Pause
                          </button>
                          <button
                            className="bdl-skip"
                            onClick={() => noAnswer("skipped")}
                          >
                            Skip {I.chevR({ size: 14 })}
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  )}

                  {(stage === "ringing" || stage === "live") && (
                    <React.Fragment>
                      <div className="dlr-num">{cur.number}</div>
                      {pad ? <div className="dlr-dtmf">{pad}</div> : null}
                      <div className={"dlr-status is-" + stage}>
                        {reconnecting
                          ? "Reconnecting…"
                          : stage === "live"
                            ? dlFmt(sec)
                            : "Ringing…"}
                      </div>
                      {reconnecting && (
                        <div className="dlr-netdrop">
                          {I.info({ size: 12 })} Connection lost. Trying to get
                          the call back — it will end if this does not clear.
                        </div>
                      )}
                      <div className="dlr-controls">
                        <button
                          className={"dlr-ctl" + (muted ? " is-on" : "")}
                          onClick={() => setMuted((m) => !m)}
                        >
                          {muted ? I.micOff({ size: 18 }) : I.mic({ size: 18 })}
                          <span>{muted ? "Unmute" : "Mute"}</span>
                        </button>
                        <button
                          className={"dlr-ctl" + (showPad ? " is-on" : "")}
                          onClick={() => setShowPad((s) => !s)}
                        >
                          {I.keypad({ size: 18 })}
                          <span>Keypad</span>
                        </button>
                        <button
                          className={"dlr-ctl" + (held ? " is-on" : "")}
                          onClick={toggleHold}
                        >
                          {held
                            ? I.pauseOff({ size: 18 })
                            : I.pause({ size: 18 })}
                          <span>Hold</span>
                        </button>
                      </div>
                      <div className="bdl-callrow">
                        <button
                          className="dlr-hang"
                          onClick={hangUp}
                          aria-label="End call"
                        >
                          {I.phoneOff({ size: 22 })}
                        </button>
                      </div>
                      {nextCur && (
                        <div className="bdl-upnext">
                          Up next · {nextCur.person.name}
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </div>
            {showPad && (stage === "ringing" || stage === "live") && (
              <div className="dlr-pad dlr-pad-drawer">
                {keys.map((k) => (
                  <button
                    className="dlr-key"
                    key={k}
                    onClick={() => {
                      setPad((p) => pushDigit(p, k))
                      sendDigits(k)
                    }}
                  >
                    <span className="dlr-key-n">{k}</span>
                    <span className="dlr-key-s">{sub[k] || " "}</span>
                  </button>
                ))}
                <button
                  className="dlr-padback"
                  onClick={() => setPad((p) => p.slice(0, -1))}
                  disabled={!pad}
                >
                  {I.backspace({ size: 15 })} Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {manualDialer}
      {endConfirm}
      {showProfile && (
        <PersonBriefHost
          person={profilePerson || cur.person}
          onClose={() => setShowProfile(false)}
        />
      )}
    </React.Fragment>
  )
}

export function PeopleDialerDemo() {
  const [tab, setTab] = useState("list")
  const list =
    DEMO_LISTS.find(
      (l) => l.id === (tab === "list" ? "priority-champions" : "recommended")
    ) || DEMO_LISTS[0]
  const [selected, setSelected] = useState(() => new Set())
  const startBulk = useVoiceSessionStore((s) => s.startBulk)
  const startSingle = useVoiceSessionStore((s) => s.startSingle)

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) {
        n.delete(id)
      } else {
        n.add(id)
      }
      return n
    })
  const chosen = list.people.filter((p) => selected.has(p.id))

  return (
    <div className="voice-dialer-root voice-dialer-page">
      <div className="pdemo">
        <h1>People</h1>
        <p className="pdemo-sub">
          Select contacts and power-dial through them, or call one directly.
          (Demo data — the People workspace is built separately.)
        </p>

        <div className="pdemo-tabs">
          <button
            className={"pdemo-tab" + (tab === "list" ? " is-on" : "")}
            onClick={() => {
              setTab("list")
              setSelected(new Set())
            }}
          >
            People list
          </button>
          <button
            className={"pdemo-tab" + (tab === "rec" ? " is-on" : "")}
            onClick={() => {
              setTab("rec")
              setSelected(new Set())
            }}
          >
            Recommended people
          </button>
        </div>

        <div className="pdemo-listhd">
          <span className="pdemo-listnm">
            {list.kind === "priority"
              ? I.crown({ size: 15 })
              : I.sparkle({ size: 15 })}{" "}
            {list.name}
          </span>
          <span className="pdemo-count">
            {list.people.length} contacts · {chosen.length} selected
          </span>
          <span className="pdemo-bar">
            <button
              className="pdemo-power"
              disabled={chosen.length === 0}
              onClick={() => startBulk(chosen)}
            >
              {I.phone({ size: 14 })} Power Dial · {chosen.length}
            </button>
          </span>
        </div>

        <div className="pdemo-list">
          {list.people.map((p) => (
            <div
              key={p.id}
              className={"pdemo-row" + (selected.has(p.id) ? " is-sel" : "")}
              onClick={() => toggle(p.id)}
            >
              <span className="pdemo-check">
                {selected.has(p.id) ? I.check({ size: 13 }) : null}
              </span>
              <span className="pdemo-av">{dlInit(p.name)}</span>
              <span className="pdemo-main">
                <span className="pdemo-nm">{p.name}</span>
                <span className="pdemo-role">
                  {p.title} · {p.account.name}
                </span>
              </span>
              {p.phone ? (
                <span className="pdemo-phone">{p.phone}</span>
              ) : (
                <span className="pdemo-nophone">No number</span>
              )}
              <button
                className="pdemo-call"
                onClick={(e) => {
                  e.stopPropagation()
                  startSingle(p)
                }}
              >
                {I.phone({ size: 12 })} Call
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
